<template>
  <display-modal disablebg="true" v-if="appStore.state.promptUnsaveModal">
    <unsaved-changes></unsaved-changes>
  </display-modal>

  <recently-deleted-inventory-panel
    :open="recentlyDeletedOpen"
    :can-restore="canRestoreDeletedInventory"
    @close="recentlyDeletedOpen = false"
  ></recently-deleted-inventory-panel>

  <section class="mp-redesign" aria-label="Manage Products">
    <header class="mp-redesign__toolbar">
      <div class="mp-redesign__search">
        <search-bar
          :size="'w-full'"
          placeholder="Find puck, box, or grid"
          @pasted="handleSearch"
        ></search-bar>
      </div>
      <div class="mp-redesign__toolbar-actions">
        <nav class="mp-redesign__path-strip" aria-label="Selected inventory path" :title="selectedPath.join(' / ')">
          <span class="material-symbols-outlined" aria-hidden="true">account_tree</span>
          <span v-for="part in selectedPath" :key="part">{{ part }}</span>
        </nav>
        <span class="mp-redesign__sync" :data-state="connectionState">
          <span class="material-symbols-outlined" aria-hidden="true">{{ connectionIcon }}</span>
          {{ connectionLabel }}
        </span>
        <button
          type="button"
          class="mp-redesign__icon-button"
          title="Refresh Inventory"
          @click="refreshInventory"
        >
          <span class="material-symbols-outlined" aria-hidden="true">sync</span>
        </button>
        <button
          type="button"
          class="mp-redesign__button"
          title="Recently Deleted"
          @click="recentlyDeletedOpen = true"
        >
          <span class="material-symbols-outlined" aria-hidden="true">restore_from_trash</span>
          Recently Deleted
        </button>
      </div>
    </header>

    <div class="mp-redesign__layout">
      <aside class="mp-redesign__rail" aria-label="Inventory hierarchy">
        <header class="mp-redesign__rail-header">
          <div>
            <p class="mp-redesign__eyebrow">Hierarchy</p>
            <h2>Lab Inventory</h2>
          </div>
          <button
            type="button"
            class="mp-redesign__icon-button mp-redesign__icon-button--danger"
            title="Open recently deleted inventory"
            @click="recentlyDeletedOpen = true"
          >
            <span class="material-symbols-outlined" aria-hidden="true">delete</span>
          </button>
          <div class="mp-redesign__rail-actions">
            <span class="mp-redesign__count-chip">{{ dewars.length }} dewar{{ dewars.length === 1 ? "" : "s" }}</span>
            <button
              type="button"
              class="mp-redesign__mini-command"
              title="Create Dewar"
              :disabled="!canCreateTopLevel"
              @click="createDewar"
            >
              <span class="material-symbols-outlined" aria-hidden="true">add</span>
              Dewar
            </button>
          </div>
        </header>

        <div v-if="hierarchyTree.length" class="mp-redesign__tree-list">
          <section v-for="node in hierarchyTree" :key="canonicalId(node.dewar)" class="mp-redesign__tree-group">
            <div class="mp-redesign__tree-row-shell mp-redesign__dewar-shell" :class="{ active: canonicalId(node.dewar) === selectedDewarId }">
              <button
                type="button"
                class="mp-redesign__collapse-toggle"
                :aria-expanded="!isDewarCollapsed(canonicalId(node.dewar))"
                :title="isDewarCollapsed(canonicalId(node.dewar)) ? 'Expand dewar' : 'Collapse dewar'"
                @click="toggleDewar(canonicalId(node.dewar))"
              >
                <span class="material-symbols-outlined" aria-hidden="true">
                  {{ isDewarCollapsed(canonicalId(node.dewar)) ? "chevron_right" : "expand_more" }}
                </span>
              </button>
              <button
                type="button"
                class="mp-redesign__tree-row mp-redesign__dewar-row"
                :title="entityTitle(node.dewar, 'Dewar')"
                @click="selectDewar(canonicalId(node.dewar))"
              >
                <span class="mp-redesign__asset-badge" aria-hidden="true">
                  <img src="/assets/images/dewar.svg" alt="" draggable="false" />
                </span>
                <span class="mp-redesign__row-copy">
                  <strong>{{ entityTitle(node.dewar, "Dewar") }}</strong>
                  <small>{{ node.racks.length }} rack{{ node.racks.length === 1 ? "" : "s" }}</small>
                </span>
                <span v-if="isReadOnly('dewars', node.dewar)" class="mp-redesign__lock">
                  <span class="material-symbols-outlined" aria-hidden="true">lock</span>
                </span>
              </button>
              <div v-if="canonicalId(node.dewar) === selectedDewarId" class="mp-redesign__row-actions">
                <button
                  type="button"
                  :disabled="!canEdit('dewars', node.dewar)"
                  title="Edit selected dewar"
                  @click="openDewarEditor(canonicalId(node.dewar))"
                >
                  <span class="material-symbols-outlined" aria-hidden="true">edit</span>
                  <span>Edit</span>
                </button>
                <button
                  type="button"
                  :disabled="!canEdit('dewars', node.dewar)"
                  title="Create rack in selected dewar"
                  @click="createRack"
                >
                  <span class="material-symbols-outlined" aria-hidden="true">view_module</span>
                  <span>Racks</span>
                </button>
              </div>
            </div>

            <div v-show="!isDewarCollapsed(canonicalId(node.dewar))" class="mp-redesign__child-list">
              <div v-for="rackNode in node.racks" :key="canonicalId(rackNode.rack)" class="mp-redesign__rack-node">
                <div class="mp-redesign__tree-row-shell mp-redesign__child-shell" :class="{ active: canonicalId(rackNode.rack) === selectedRackId }">
                  <button
                    type="button"
                    class="mp-redesign__tree-row mp-redesign__child-row"
                    :title="entityTitle(rackNode.rack, 'Rack')"
                    @click="selectRack(canonicalId(rackNode.rack))"
                  >
                    <span class="material-symbols-outlined" aria-hidden="true">view_module</span>
                    <span class="mp-redesign__row-copy">
                      <strong>{{ entityTitle(rackNode.rack, "Rack") }}</strong>
                      <small>{{ rackNode.rack.rackType || "Storage" }} - {{ rackNode.pucks.length }} puck{{ rackNode.pucks.length === 1 ? "" : "s" }}</small>
                    </span>
                    <span v-if="isReadOnly('racks', rackNode.rack)" class="mp-redesign__lock">
                      <span class="material-symbols-outlined" aria-hidden="true">lock</span>
                    </span>
                  </button>
                  <div v-if="canonicalId(rackNode.rack) === selectedRackId" class="mp-redesign__row-actions mp-redesign__row-actions--compact">
                    <button
                      type="button"
                      :disabled="!canEdit('racks', rackNode.rack)"
                      title="Edit selected rack"
                      @click="openRackEditor(canonicalId(rackNode.rack))"
                    >
                      <span class="material-symbols-outlined" aria-hidden="true">edit</span>
                      <span>Edit</span>
                    </button>
                    <button
                      type="button"
                      :disabled="!canEdit('racks', rackNode.rack)"
                      title="Create puck in selected rack"
                      @click="createPuck"
                    >
                      <span class="material-symbols-outlined" aria-hidden="true">album</span>
                      <span>Pucks</span>
                    </button>
                  </div>
                </div>

                <div
                  v-for="puck in rackNode.pucks"
                  :key="canonicalId(puck)"
                  class="mp-redesign__tree-row-shell mp-redesign__puck-shell-row"
                  :class="{ active: canonicalId(puck) === selectedPuckId }"
                >
                  <button
                    type="button"
                    class="mp-redesign__tree-row mp-redesign__puck-row"
                    :title="entityTitle(puck, 'Puck')"
                    @click="selectPuck(canonicalId(puck))"
                  >
                    <span class="mp-redesign__puck-dot" :style="{ backgroundColor: puckColor(puck) }" aria-hidden="true"></span>
                    <span class="mp-redesign__row-copy">
                      <strong>{{ entityTitle(puck, "Puck") }}</strong>
                      <small>{{ occupiedWellCount(puck) }} / {{ wellCount(puck) }} wells</small>
                    </span>
                    <span v-if="isReadOnly('pucks', puck)" class="mp-redesign__lock">
                      <span class="material-symbols-outlined" aria-hidden="true">lock</span>
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </section>
        </div>
        <div v-else class="mp-redesign__empty-stage">
          <span class="material-symbols-outlined" aria-hidden="true">inventory_2</span>
          <p>No matching inventory.</p>
        </div>
      </aside>

      <main class="mp-redesign__workbench" aria-label="Puck workbench">
        <section v-if="selectedRack" class="mp-redesign__rack-stage" aria-label="Rack contents">
          <header class="mp-redesign__rack-stage-header">
            <div>
              <p class="mp-redesign__eyebrow">Pucks</p>
              <h2>Rack contents</h2>
            </div>
            <strong>{{ pucksForSelectedRack.length }} / {{ Number(selectedRack.capacity || 10) }} used</strong>
          </header>
          <div class="mp-redesign__puck-strip" :class="{ 'mp-redesign__puck-strip--selected': selectedPuck }">
            <button
              v-for="slot in puckSlotsForSelectedRack"
              :key="`puck-slot-${slot.position}`"
              type="button"
              class="mp-redesign__puck-card"
              :class="{
                'mp-redesign__puck-card--active': slot.puck && canonicalId(slot.puck) === selectedPuckId,
                'mp-redesign__puck-card--empty': !slot.puck,
              }"
              :style="{
                '--puck-color': slot.puck ? puckColor(slot.puck) : '#dbe4eb',
                order: selectedPuck && slot.puck && canonicalId(slot.puck) === selectedPuckId ? 0 : slot.position,
              }"
              :disabled="!slot.puck"
              @click="slot.puck && selectPuck(canonicalId(slot.puck))"
            >
              <span class="mp-redesign__puck-card-art" aria-hidden="true">
                <puck-silhouette
                  v-if="slot.puck"
                  :color="slot.puck.color || 'chrome'"
                  :selected-well="0"
                  :well-rows="miniPuckWellRows(slot.puck)"
                  :interactive="false"
                />
                <span v-else class="mp-redesign__mini-puck-empty"></span>
              </span>
              <strong>{{ slot.puck ? entityTitle(slot.puck, "Puck") : `Slot ${slot.position}` }}</strong>
              <small>{{ slot.puck ? `${occupiedWellCount(slot.puck)}/${wellCount(slot.puck)} wells` : "Empty" }}</small>
            </button>
          </div>
        </section>

        <section class="mp-redesign__stage">
          <div class="mp-redesign__stage-header">
            <div>
              <p class="mp-redesign__eyebrow">Puck</p>
              <h1>{{ selectedPuck ? entityTitle(selectedPuck, "Puck") : "No puck selected" }}</h1>
              <span v-if="selectedPuck" class="mp-redesign__stage-summary">
                {{ selectedPuck.puckType || selectedPuck.type || "Puck" }} - {{ occupiedWellCount(selectedPuck) }}/{{ wellCount(selectedPuck) }} occupied wells
              </span>
              <span v-else class="mp-redesign__stage-summary">No puck assigned to the selected rack</span>
            </div>
            <div class="mp-redesign__stage-actions">
              <button
                v-if="selectedPuck"
                type="button"
                class="mp-redesign__edit-puck-command"
                title="Edit selected puck"
                :disabled="!canEdit('pucks', selectedPuck)"
                @click="openPuckEditor(canonicalId(selectedPuck))"
              >
                <span class="material-symbols-outlined" aria-hidden="true">edit</span>
                Edit Puck
              </button>
              <span class="mp-redesign__selected-well-chip" :class="{ empty: !selectedPuck }">
                {{ selectedPuck ? `Well ${selectedWellNo}` : "No well selected" }}
              </span>
            </div>
          </div>

          <div v-if="selectedPuck" class="mp-redesign__puck-stage">
            <div class="mp-redesign__puck-visual" aria-label="Puck well map">
              <puck-silhouette
                :color="selectedPuck.color || 'chrome'"
                :puck-id="canonicalId(selectedPuck)"
                :can-move-boxes="canEdit('pucks', selectedPuck)"
                :selected-well="selectedWellNo"
                :occupied-wells="occupiedWellNumbers"
                :well-rows="puckSilhouetteWellRows"
                :show-box-title-preview="true"
                @select-well="selectWell"
                @move-box="handlePuckSilhouetteMove"
              />
            </div>
            <div class="mp-redesign__well-table" aria-label="Puck wells">
              <div class="mp-redesign__well-table-head">
                <span>Well</span>
                <span>Box</span>
                <span>Grids</span>
              </div>
              <button
                v-for="well in wellTiles"
                :key="`row-${well.no}`"
                type="button"
                class="mp-redesign__well-row"
                :class="{ 'mp-redesign__well-row--active': selectedWellNo === well.no }"
                @click="selectWell(well.no)"
              >
                <span class="mp-redesign__well-no">{{ well.no }}</span>
                <strong>{{ well.box ? entityTitle(well.box, "Box") : "Empty well" }}</strong>
                <small>{{ well.box ? getGridsForBox(canonicalId(well.box)).length : 0 }}</small>
              </button>
            </div>
          </div>
          <div v-else class="mp-redesign__empty-stage">
            <span class="material-symbols-outlined" aria-hidden="true">inventory_2</span>
            <p>Select a dewar, rack, and puck.</p>
          </div>
        </section>

      </main>

      <aside class="mp-redesign__inspector" aria-label="Well inspector">
        <section class="mp-redesign__detail-panel">
          <div class="mp-redesign__detail-header">
            <div>
              <p class="mp-redesign__eyebrow">Inspector</p>
              <h2>{{ selectedWellNo ? `Well ${selectedWellNo}` : "No well" }}</h2>
              <span class="mp-redesign__selected-summary">
                {{ selectedPuck ? selectedBox ? entityTitle(selectedBox, "Box") : "Empty well" : "No active well" }}
              </span>
            </div>
            <span
              class="mp-redesign__occupancy-chip"
              :class="{ empty: !selectedBox, unavailable: !selectedPuck }"
            >
              {{ selectedPuck ? selectedBox ? "Occupied" : "Empty" : "Unavailable" }}
            </span>
          </div>

          <nav class="mp-redesign__tabs" aria-label="Inspector sections">
            <button
              v-for="tab in inspectorTabs"
              :key="tab.id"
              type="button"
              :class="{ 'mp-redesign__tab--active': inspectorTab === tab.id }"
              :disabled="tab.disabled"
              :title="tab.title"
              @click="inspectorTab = tab.id"
            >
              {{ tab.label }}
            </button>
          </nav>

          <div v-if="selectedPuck && selectedWellNo && inspectorTab === 'box'">
            <div class="mp-redesign__box-preview" aria-label="Box preview">
              <component
                :is="boxVisualComponent(boxPreviewEntity)"
                class="mp-redesign__box-preview-svg"
                :class="boxVisualClass(boxPreviewEntity)"
                :style="{ '--box-color': boxColor(boxPreviewEntity), '--box-dark-color': boxDarkColor(boxPreviewEntity) }"
                :color="boxPreviewEntity.color || 'gold'"
                :dark-color="boxDarkColor(boxPreviewEntity)"
                :show-grid-clips="boxGridClipState(boxPreviewEntity)"
                aria-hidden="true"
              />
            </div>

            <button
              v-if="!selectedBox"
              type="button"
              class="mp-redesign__create-box"
              :disabled="!canEdit('pucks', selectedPuck)"
              @click="createBox"
            >
              <span class="material-symbols-outlined" aria-hidden="true">add_box</span>
              Create Box in Well {{ selectedWellNo }}
            </button>
            <div class="mp-redesign__action-strip" aria-label="Box actions">
              <button
                type="button"
                class="mp-redesign__button"
                :disabled="!selectedBox"
                title="Copy current box settings"
                @click="copySelectedBox"
              >
                Copy Box
              </button>
              <button
                type="button"
                class="mp-redesign__button"
                :disabled="!canPasteBox"
                :title="canPasteBox ? 'Paste copied box into this well' : 'Copy a box and select an empty editable well'"
                @click="pasteCopiedBox"
              >
                Paste Box
              </button>
              <button
                type="button"
                class="mp-redesign__button mp-redesign__button--danger"
                :disabled="selectedBox ? !canEdit('boxes', selectedBox) : true"
                @click="deleteSelectedBox"
              >
                Delete Box
              </button>
            </div>

            <section class="mp-redesign__box-preferences" aria-label="Box preferences">
              <div class="mp-redesign__preferences-header">
                <div>
                  <strong>Box Preferences</strong>
                  <span>Templates and grid numbering</span>
                </div>
              </div>
              <label class="mp-redesign__template-select">
                <span>Available Templates</span>
                <select :disabled="!boxSettingTemplates.length" @change="applyBoxTemplate($event.target.value)">
                  <option value="">{{ boxSettingTemplates.length ? "Select a template" : "No saved templates" }}</option>
                  <option v-for="template in boxSettingTemplates" :key="canonicalId(template)" :value="canonicalId(template)">
                    {{ template.name || template.title || canonicalId(template) }}
                  </option>
                </select>
              </label>
              <fieldset class="mp-redesign__numbering-options">
                <legend>Grid Numbering</legend>
                <label v-for="option in gridNumberingOptions" :key="option" class="mp-redesign__numbering-option">
                  <input
                    type="radio"
                    name="box-grid-numbering"
                    :value="option"
                    :checked="Number(boxDraft.gridNumbering || 1) === option"
                    :disabled="selectedBox ? !canEdit('boxes', selectedBox) : !canEdit('pucks', selectedPuck)"
                    @change="setBoxDraftField('gridNumbering', option)"
                  />
                  <span>{{ gridNumberingLabel(option) }}</span>
                </label>
              </fieldset>
              <div class="mp-redesign__color-swatch-grid" role="radiogroup" aria-label="Box color">
                <button
                  v-for="color in boxColors"
                  :key="color"
                  type="button"
                  class="mp-redesign__color-swatch"
                  :class="{ 'mp-redesign__color-swatch--active': (boxDraft.color || 'gold') === color }"
                  :style="{ '--swatch': boxColor({ color }), '--swatch-dark': boxDarkColor({ color }) }"
                  :disabled="selectedBox ? !canEdit('boxes', selectedBox) : !canEdit('pucks', selectedPuck)"
                  :title="color"
                  @click="setBoxDraftField('color', color)"
                >
                  <span></span>
                </button>
              </div>
            </section>

            <div v-if="selectedBox" class="mp-redesign__box-editor">
              <div class="mp-redesign__form-grid mp-redesign__form-grid--single">
                <label>
                  <span>Box Name</span>
                  <input
                    v-model="boxDraft.title"
                    :disabled="!canEdit('boxes', selectedBox)"
                    @input="queueAutosave('boxes', 'title')"
                  />
                </label>
                <label>
                  <span>Custom ID</span>
                  <input
                    v-model="boxDraft.customId"
                    :disabled="!canEdit('boxes', selectedBox)"
                    @input="queueAutosave('boxes', 'customId')"
                  />
                </label>
                <label>
                  <span>Numbering</span>
                  <select
                    v-model.number="boxDraft.gridNumbering"
                    :disabled="!canEdit('boxes', selectedBox)"
                    @change="queueAutosave('boxes', 'gridNumbering')"
                  >
                    <option v-for="option in gridNumberingOptions" :key="option" :value="option">
                      {{ option }}
                    </option>
                  </select>
                </label>
                <label>
                  <span>Color</span>
                  <select
                    v-model="boxDraft.color"
                    :disabled="!canEdit('boxes', selectedBox)"
                    @change="queueAutosave('boxes', 'color')"
                  >
                    <option :value="null">default</option>
                    <option v-for="color in boxColors" :key="color" :value="color">
                      {{ color }}
                    </option>
                  </select>
                </label>
              </div>
              <div class="mp-redesign__move-panel" aria-label="Move box">
                <div class="mp-redesign__move-header">
                  <strong>Move Box</strong>
                  <label class="mp-redesign__checkbox mp-redesign__checkbox--inline">
                    <input v-model="moveFollowTarget" type="checkbox" :disabled="!canEditMoveTarget" />
                    <span>Follow</span>
                  </label>
                </div>
                <label>
                  <span>Dewar</span>
                  <select v-model="moveTargetDewarId" :disabled="!canEditMoveTarget">
                    <option v-for="dewar in moveTargetDewarOptions" :key="canonicalId(dewar)" :value="canonicalId(dewar)">
                      {{ entityTitle(dewar, "Dewar") }}
                    </option>
                  </select>
                </label>
                <label>
                  <span>Rack</span>
                  <select v-model="moveTargetRackId" :disabled="!canEditMoveTarget">
                    <option v-for="rack in moveTargetRackOptions" :key="canonicalId(rack)" :value="canonicalId(rack)">
                      {{ entityTitle(rack, "Rack") }}
                    </option>
                  </select>
                </label>
                <label>
                  <span>Puck</span>
                  <select v-model="moveTargetPuckId" :disabled="!canEditMoveTarget">
                    <option v-for="puck in moveTargetPuckOptions" :key="canonicalId(puck)" :value="canonicalId(puck)">
                      {{ entityTitle(puck, "Puck") }}
                    </option>
                  </select>
                </label>
                <label>
                  <span>Well</span>
                  <select v-model.number="moveTargetWell" :disabled="!canEditMoveTarget">
                    <option v-for="well in moveTargetWellOptions" :key="well" :value="well">
                      Well {{ well }}
                    </option>
                  </select>
                </label>
                <button
                  type="button"
                  class="mp-redesign__button"
                  :disabled="!canMoveBox"
                  :title="canMoveBox ? 'Move selected box' : 'Select an editable box and open target well'"
                  @click="moveSelectedBox"
                >
                  <span class="material-symbols-outlined" aria-hidden="true">drive_file_move</span>
                  Move
                </button>
              </div>
            </div>
          </div>
        </section>

        <section v-if="inspectorTab === 'grids'" class="mp-redesign__detail-panel">
          <div class="mp-redesign__detail-header">
            <div>
              <p class="mp-redesign__eyebrow">Grid Slots</p>
              <h2>{{ selectedBox ? entityTitle(selectedBox, "Box") : "No box" }}</h2>
            </div>
            <span class="mp-redesign__status" :data-state="selectedGridStatus.status">
              {{ statusLabel(selectedGridStatus) }}
            </span>
          </div>

          <div v-if="selectedBox" class="mp-redesign__grid-slots">
            <button
              v-for="slot in gridSlots"
              :key="slot.position"
              type="button"
              class="mp-redesign__grid-slot"
              :class="{ 'mp-redesign__grid-slot--active': selectedGridPosition === slot.position }"
              @click="selectedGridPosition = slot.position"
            >
              <span>{{ slot.label }}</span>
              <strong>{{ slot.grid ? entityTitle(slot.grid, "Grid") : "Empty" }}</strong>
            </button>
          </div>

          <div v-if="selectedBox && selectedGrid" class="mp-redesign__grid-editor">
            <div class="mp-redesign__form-grid mp-redesign__form-grid--single">
              <label>
                <span>Name</span>
                <input
                  v-model="gridDraft.name"
                  :disabled="!canEdit('grids', selectedGrid)"
                  @input="queueAutosave('grids', 'name')"
                />
              </label>
              <label>
                <span>Catalog Type</span>
                <input
                  v-model="gridDraft.gridCatalogType"
                  :disabled="!canEdit('grids', selectedGrid)"
                  @input="queueAutosave('grids', 'gridCatalogType')"
                />
              </label>
              <label class="mp-redesign__checkbox">
                <input
                  v-model="gridDraft.forTomography"
                  type="checkbox"
                  :disabled="!canEdit('grids', selectedGrid)"
                  @change="queueAutosave('grids', 'forTomography')"
                />
                <span>Tomography</span>
              </label>
            </div>
            <div class="mp-redesign__inspector-actions">
              <button
                type="button"
                class="mp-redesign__button"
                :disabled="!selectedGrid"
                title="Copy current grid settings"
                @click="copySelectedGrid"
              >
                <span class="material-symbols-outlined" aria-hidden="true">content_copy</span>
                Copy
              </button>
              <button
                type="button"
                class="mp-redesign__button"
                :disabled="!canPasteGrid"
                :title="canPasteGrid ? 'Paste copied grid into this slot' : 'Copy a grid and select an empty editable grid slot'"
                @click="pasteCopiedGrid"
              >
                <span class="material-symbols-outlined" aria-hidden="true">content_paste</span>
                Paste
              </button>
              <button
                type="button"
                class="mp-redesign__button mp-redesign__button--danger"
                :disabled="!canEdit('grids', selectedGrid)"
                @click="deleteSelectedGrid"
              >
                <span class="material-symbols-outlined" aria-hidden="true">archive</span>
                Archive Grid
              </button>
            </div>
          </div>

          <button
            v-else-if="selectedBox"
            type="button"
            class="mp-redesign__create-box"
            :disabled="!canEdit('boxes', selectedBox)"
            @click="createGrid"
          >
            <span class="material-symbols-outlined" aria-hidden="true">add</span>
            Create Grid
          </button>

          <div v-if="selectedBox" class="mp-redesign__import-panel">
            <label>
              <span>Import Archived Grid</span>
              <select v-model="archivedGridImportId" :disabled="!canChooseArchivedGrid">
                <option value="">{{ archivedGridOptions.length ? "Select archived grid" : "No archived grids" }}</option>
                <option v-for="grid in archivedGridOptions" :key="canonicalId(grid)" :value="canonicalId(grid)">
                  {{ entityTitle(grid, "Archived grid") }}
                </option>
              </select>
            </label>
            <button
              type="button"
              class="mp-redesign__button"
              :disabled="!canImportArchivedGrid"
              @click="importArchivedGrid"
            >
              <span class="material-symbols-outlined" aria-hidden="true">unarchive</span>
              Import
            </button>
          </div>

          <p v-else class="mp-redesign__empty">Select a box to manage grids.</p>
        </section>

        <section v-if="inspectorTab === 'lamellae'" class="mp-redesign__detail-panel">
          <div class="mp-redesign__detail-header">
            <div>
              <p class="mp-redesign__eyebrow">Lamellae</p>
              <h2>{{ selectedGrid ? entityTitle(selectedGrid, "Grid") : "No grid" }}</h2>
            </div>
            <span class="mp-redesign__status" :data-state="selectedGridStatus.status">
              {{ selectedLamellaSummary.total }} total
            </span>
          </div>
          <div class="mp-redesign__lamella-summary">
            <span><strong>{{ selectedLamellaSummary.active }}</strong>Active</span>
            <span><strong>{{ selectedLamellaSummary.completed }}</strong>Complete</span>
            <span><strong>{{ selectedLamellaSummary.alert }}</strong>Alert</span>
          </div>
          <button
            type="button"
            class="mp-redesign__create-box"
            :disabled="!selectedGrid || !canEdit('grids', selectedGrid)"
            @click="createLamellaForSelectedGrid"
          >
            <span class="material-symbols-outlined" aria-hidden="true">add</span>
            Add Lamella
          </button>
          <div v-if="selectedLamellae.length" class="mp-redesign__lamella-list">
            <article v-for="lamella in selectedLamellae" :key="canonicalId(lamella)" class="mp-redesign__lamella-row">
              <div>
                <strong>{{ entityTitle(lamella, "Lamella") }}</strong>
                <small>{{ lamella.currentStage || "No stage" }}</small>
              </div>
              <select
                :value="lamella.status || 'pending'"
                :disabled="!selectedGrid || !canEdit('grids', selectedGrid)"
                @change="updateLamellaStatus(canonicalId(lamella), $event.target.value)"
              >
                <option value="pending">Pending</option>
                <option value="milling">Milling</option>
                <option value="complete">Complete</option>
                <option value="error">Error</option>
              </select>
            </article>
          </div>
          <p v-else class="mp-redesign__empty">No lamellae for this grid.</p>
          <lamella-section
            v-if="selectedGrid"
            :grid-id="canonicalId(selectedGrid)"
            :grid-name="entityTitle(selectedGrid, 'Grid')"
            :readonly="!canEdit('grids', selectedGrid)"
            auto-expand
          ></lamella-section>
        </section>

        <section v-if="inspectorTab === 'history'" class="mp-redesign__detail-panel">
          <div class="mp-redesign__detail-header">
            <div>
              <p class="mp-redesign__eyebrow">History</p>
              <h2>{{ selectedBox ? entityTitle(selectedBox, "Box") : "No box" }}</h2>
            </div>
          </div>
          <div class="mp-redesign__empty-stage">
            <span class="material-symbols-outlined" aria-hidden="true">history</span>
            <p>No history entries loaded for this well.</p>
          </div>
        </section>
      </aside>
    </div>

    <div
      v-if="entityEditorMode"
      class="mp-redesign__drawer-layer"
      role="presentation"
      @click.self="closeEntityEditor"
    >
      <aside
        class="mp-redesign__entity-drawer"
        role="dialog"
        aria-modal="true"
        :aria-label="entityEditorTitle"
      >
        <header class="mp-redesign__drawer-header">
          <div>
            <p class="mp-redesign__eyebrow">{{ entityEditorEyebrow }}</p>
            <h2>{{ entityEditorTitle }}</h2>
            <span>{{ entityEditorSubtitle }}</span>
          </div>
          <div class="mp-redesign__drawer-header-actions">
            <span class="mp-redesign__status" :data-state="entityEditorStatus.status">
              {{ statusLabel(entityEditorStatus) }}
            </span>
            <button
              type="button"
              class="mp-redesign__icon-button"
              aria-label="Close editor"
              title="Close"
              @click="closeEntityEditor"
            >
              <span class="material-symbols-outlined" aria-hidden="true">close</span>
            </button>
          </div>
        </header>

        <form
          v-if="entityEditorMode === 'dewars' && selectedDewar"
          class="mp-redesign__drawer-form"
          @submit.prevent
          @focusout="flushEntityEditorAutosave"
        >
          <label>
            <span>Name</span>
            <input
              v-model="dewarDraft.title"
              :disabled="!canEdit('dewars', selectedDewar)"
              autocomplete="off"
              @input="queueAutosave('dewars', 'title')"
            />
          </label>
          <label>
            <span>Custom ID</span>
            <input
              v-model="dewarDraft.customId"
              :disabled="!canEdit('dewars', selectedDewar)"
              autocomplete="off"
              @input="queueAutosave('dewars', 'customId')"
            />
          </label>
          <label>
            <span>Capacity</span>
            <input
              v-model.number="dewarDraft.capacity"
              type="number"
              min="1"
              :disabled="!canEdit('dewars', selectedDewar)"
              @input="queueAutosave('dewars', 'capacity')"
            />
          </label>
          <label>
            <span>Serial</span>
            <input
              v-model="dewarDraft.serialNo"
              :disabled="!canEdit('dewars', selectedDewar)"
              autocomplete="off"
              @input="queueAutosave('dewars', 'serialNo')"
            />
          </label>
        </form>

        <form
          v-else-if="entityEditorMode === 'racks' && selectedRack"
          class="mp-redesign__drawer-form"
          @submit.prevent
          @focusout="flushEntityEditorAutosave"
        >
          <label>
            <span>Name</span>
            <input
              v-model="rackDraft.title"
              :disabled="!canEdit('racks', selectedRack)"
              autocomplete="off"
              @input="queueAutosave('racks', 'title')"
            />
          </label>
          <label>
            <span>Position</span>
            <input
              v-model.number="rackDraft.dewarPosition"
              type="number"
              min="1"
              :disabled="!canEdit('racks', selectedRack)"
              @input="queueAutosave('racks', 'dewarPosition')"
            />
          </label>
          <label>
            <span>Type</span>
            <select
              v-model="rackDraft.rackType"
              :disabled="!canEdit('racks', selectedRack)"
              @change="queueAutosave('racks', 'rackType')"
            >
              <option value="Storage">Storage</option>
              <option value="Transport">Transport</option>
            </select>
          </label>
          <label>
            <span>Serial</span>
            <input
              v-model="rackDraft.serialNo"
              :disabled="!canEdit('racks', selectedRack)"
              autocomplete="off"
              @input="queueAutosave('racks', 'serialNo')"
            />
          </label>
        </form>

        <form
          v-else-if="entityEditorMode === 'pucks' && selectedPuck"
          class="mp-redesign__drawer-form"
          @submit.prevent
          @focusout="flushEntityEditorAutosave"
        >
          <label>
            <span>Name</span>
            <input
              v-model="puckDraft.title"
              :disabled="!canEdit('pucks', selectedPuck)"
              autocomplete="off"
              @input="queueAutosave('pucks', 'title')"
            />
          </label>
          <label>
            <span>Wells</span>
            <input
              v-model.number="puckDraft.wellCount"
              type="number"
              min="1"
              max="16"
              :disabled="!canEdit('pucks', selectedPuck)"
              @input="queueAutosave('pucks', 'wellCount')"
            />
          </label>
          <label>
            <span>Color</span>
            <select
              v-model="puckDraft.color"
              :disabled="!canEdit('pucks', selectedPuck)"
              @change="queueAutosave('pucks', 'color')"
            >
              <option v-for="color in puckColors" :key="color" :value="color">
                {{ color }}
              </option>
            </select>
          </label>
          <label>
            <span>Serial</span>
            <input
              v-model="puckDraft.serialNo"
              :disabled="!canEdit('pucks', selectedPuck)"
              autocomplete="off"
              @input="queueAutosave('pucks', 'serialNo')"
            />
          </label>
        </form>

        <div v-else class="mp-redesign__empty-stage">
          <span class="material-symbols-outlined" aria-hidden="true">edit</span>
          <p>Select an editable inventory item.</p>
        </div>

        <footer class="mp-redesign__drawer-footer">
          <button type="button" class="mp-redesign__button" @click="closeEntityEditor">
            Close
          </button>
          <button
            v-if="entityEditorMode === 'pucks' && selectedPuck"
            type="button"
            class="mp-redesign__button mp-redesign__button--danger"
            :disabled="!canEdit('pucks', selectedPuck)"
            @click="deleteSelectedPuck"
          >
            Delete Puck
          </button>
        </footer>
      </aside>
    </div>
  </section>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import SearchBar from "../../ui/SearchBar.vue";
import UnsavedChanges from "../../modal-inners/unsaved-changes.vue";
import RecentlyDeletedInventoryPanel from "./manage-products-sections/RecentlyDeletedInventoryPanel.vue";
import LamellaSection from "./manage-products-sections/LamellaSection.vue";
import PuckSilhouette from "./manage-products-redesign/PuckSilhouette.vue";
import ClippedBox from "./manage-products-sections/clipped.vue";
import OpenCupBox from "./manage-products-sections/opencup.vue";
import UnclippedBox from "./manage-products-sections/unclipped.vue";
import appStore from "../../../store/index.js";
import { useInventoryStore } from "../../../stores/inventory";
import { useYjsProviderStore } from "../../../stores/yjs-provider";
import { gridSlotOrder } from "../../../utils/gridNumbering";

defineOptions({ inheritAttrs: false });
defineEmits(["openfeedback"]);

const inventoryStore = useInventoryStore();
const providerStore = useYjsProviderStore();
const route = useRoute();
const router = useRouter();

const recentlyDeletedOpen = ref(false);
const snapshotLoading = ref(false);
const selectedDewarId = ref(null);
const selectedRackId = ref(null);
const selectedPuckId = ref(null);
const selectedWellNo = ref(1);
const selectedGridPosition = ref(1);
const inspectorTab = ref("box");
const copiedBox = ref(null);
const copiedGrid = ref(null);
const archivedGridImportId = ref("");
const entityEditorMode = ref(null);
const moveTargetDewarId = ref(null);
const moveTargetRackId = ref(null);
const moveTargetPuckId = ref(null);
const moveTargetWell = ref(null);
const moveFollowTarget = ref(true);
const lastAppliedNavigation = ref(null);
const localStatuses = reactive({});
const autosaveTimers = {};
const autosaveDirtyFields = {};
const autosaveQueuedPayloads = {};

const dewarDraft = reactive({
  title: "",
  serialNo: "",
  customId: "",
  capacity: 10,
});
const rackDraft = reactive({
  title: "",
  serialNo: "",
  dewarPosition: 1,
  rackType: "Storage",
});
const puckDraft = reactive({
  title: "",
  serialNo: "",
  wellCount: 12,
  color: "chrome",
});
const boxDraft = reactive({
  title: "",
  customId: "",
  gridNumbering: 1,
  color: null,
});
const gridDraft = reactive({
  name: "",
  gridCatalogType: "",
  forTomography: false,
});
const COLLAPSED_DEWAR_IDS_KEY = "manage-products.web.collapsedDewarIds";

function loadCollapsedDewarIds() {
  if (typeof window === "undefined") return new Set();
  try {
    const raw = window.localStorage.getItem(COLLAPSED_DEWAR_IDS_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? new Set(parsed.filter((id) => typeof id === "string")) : new Set();
  } catch {
    return new Set();
  }
}

const collapsedDewarIds = ref(loadCollapsedDewarIds());

const AUTOSAVE_FIELD_BUILDERS = {
  dewars: {
    title: () => ({ title: dewarDraft.title, name: dewarDraft.title }),
    serialNo: () => ({ serialNo: dewarDraft.serialNo }),
    customId: () => ({ customId: dewarDraft.customId }),
    capacity: () => ({ capacity: Number(dewarDraft.capacity || 1) }),
  },
  racks: {
    title: () => ({ title: rackDraft.title, name: rackDraft.title }),
    serialNo: () => ({ serialNo: rackDraft.serialNo }),
    dewarPosition: () => ({
      dewarPosition: Number(rackDraft.dewarPosition || 1),
      position: Number(rackDraft.dewarPosition || 1),
    }),
    rackType: () => ({ rackType: rackDraft.rackType }),
  },
  pucks: {
    title: () => ({ title: puckDraft.title, name: puckDraft.title }),
    serialNo: () => ({ serialNo: puckDraft.serialNo }),
    wellCount: () => ({ wellCount: Number(puckDraft.wellCount || 12) }),
    color: () => ({ color: puckDraft.color || "chrome" }),
  },
  boxes: {
    title: () => ({ title: boxDraft.title }),
    customId: () => ({ customId: boxDraft.customId }),
    gridNumbering: () => ({ gridNumbering: Number(boxDraft.gridNumbering || 1) }),
    color: () => ({ color: boxDraft.color || null }),
  },
  grids: {
    name: () => ({ name: gridDraft.name }),
    gridCatalogType: () => ({ gridCatalogType: gridDraft.gridCatalogType || null }),
    forTomography: () => ({ forTomography: !!gridDraft.forTomography }),
  },
};

const AUTOSAVE_FIELD_PAYLOAD_KEYS = Object.fromEntries(
  Object.entries(AUTOSAVE_FIELD_BUILDERS).map(([type, builders]) => [
    type,
    Object.fromEntries(
      Object.entries(builders).map(([field, buildPayload]) => [
        field,
        Object.keys(buildPayload()),
      ])
    ),
  ])
);

const puckColors = computed(() => appStore.state.puckColors || []);
const boxColors = computed(() => appStore.state.boxColors || []);
const boxSettingTemplates = computed(() =>
  appStore.state.activeUser?.labId?.boxSettingTemplates || []
);
const gridNumberingOptions = [1, 2, 3, 4, 5, 6];
const gridNumberingLabels = {
  1: "Clockwise",
  2: "Counter-clockwise",
  3: "Reverse serpentine",
  4: "Mirror horizontal",
  5: "Mirror vertical",
  6: "Serpentine",
};
const canRestoreDeletedInventory = computed(() => Boolean(appStore.state.activeUser));
const canCreateTopLevel = computed(() => Boolean(appStore.state.activeUser));
const connectionState = computed(() => providerStore.connectionState || "disconnected");
const connectionIcon = computed(() => {
  if (providerStore.error) return "sync_problem";
  if (providerStore.isSynced) return "cloud_done";
  if (connectionState.value === "connecting") return "sync";
  return "cloud_off";
});
const connectionLabel = computed(() => {
  if (providerStore.error) return "Sync error";
  if (providerStore.isSynced) return "Synced";
  if (connectionState.value === "connecting") return "Connecting";
  return "Offline";
});
const usesLiveInventory = computed(() => inventoryStore.dewarsList.length > 0);

const legacyDewars = computed(() => {
  const output = [];
  (appStore.state.dewarData || []).forEach((lab) => {
    (lab?.data || []).forEach((dewar) => {
      output.push(dewar);
    });
  });
  return output;
});

const legacyRacks = computed(() =>
  legacyDewars.value.flatMap((dewar) =>
    (dewar?.racks || []).map((rack) => ({
      ...rack,
      dewarId: canonicalId(dewar),
    }))
  )
);

const legacyPucks = computed(() =>
  legacyRacks.value.flatMap((rack) =>
    (rack?.pucks || []).map((puck) => ({
      ...puck,
      rackId: canonicalId(rack),
    }))
  )
);

const legacyBoxes = computed(() => {
  const boxes = [];
  legacyPucks.value.forEach((puck) => {
    for (let wellNo = 1; wellNo <= 16; wellNo += 1) {
      const box = puck?.[`well${wellNo}`];
      if (!box || box === "hide") continue;
      boxes.push({
        ...box,
        puckId: canonicalId(puck),
        wellNo,
      });
    }
  });
  return boxes;
});

const legacyGrids = computed(() =>
  legacyBoxes.value.flatMap((box) =>
    gridSlotKeys
      .map((slotName, index) => {
        const grid = box?.[slotName];
        if (!grid) return null;
        return {
          ...grid,
          boxId: canonicalId(box),
          position: index + 1,
        };
      })
      .filter(Boolean)
  )
);

const dewars = computed(() =>
  [...(usesLiveInventory.value ? inventoryStore.dewarsList : legacyDewars.value)]
    .sort((a, b) => entityTitle(a, "").localeCompare(entityTitle(b, "")))
);
const hierarchyTree = computed(() =>
  dewars.value.map((dewar) => {
    const racks = getRacksForDewar(canonicalId(dewar))
      .sort((a, b) => Number(a.dewarPosition ?? a.position ?? 999) - Number(b.dewarPosition ?? b.position ?? 999))
      .map((rack) => ({
        rack,
        pucks: getPucksForRack(canonicalId(rack))
          .sort((a, b) => Number(a.rackPosition ?? a.position ?? 999) - Number(b.rackPosition ?? b.position ?? 999)),
      }));
    return { dewar, racks };
  })
);
const selectedDewar = computed(() => selectedDewarId.value ? getDewar(selectedDewarId.value) : null);
const selectedRack = computed(() => selectedRackId.value ? getRack(selectedRackId.value) : null);
const selectedPuck = computed(() => selectedPuckId.value ? getPuck(selectedPuckId.value) : null);
const selectedBox = computed(() =>
  selectedPuck.value && selectedWellNo.value
    ? getBoxAtWell(canonicalId(selectedPuck.value), selectedWellNo.value)
    : null
);
const boxPreviewEntity = computed(() => selectedBox.value || {
  color: boxDraft.color || "gold",
  gridNumbering: Number(boxDraft.gridNumbering || 1),
  boxType: "Clipped",
  slotCount: 4,
});
const selectedGrid = computed(() =>
  selectedBox.value
    ? getGridsForBox(canonicalId(selectedBox.value)).find(
        (grid) => Number(grid.position) === Number(selectedGridPosition.value)
      ) || null
    : null
);
const selectedLamellae = computed(() =>
  selectedGrid.value ? inventoryStore.getLamellaeForGrid(canonicalId(selectedGrid.value)) : []
);
const selectedLamellaSummary = computed(() =>
  selectedGrid.value
    ? inventoryStore.getLamellaSummaryForGrid(canonicalId(selectedGrid.value))
    : { total: 0, active: 0, completed: 0, alert: 0 }
);
const selectedPath = computed(() => [
  selectedDewar.value ? entityTitle(selectedDewar.value, "Dewar") : "No dewar",
  selectedRack.value ? entityTitle(selectedRack.value, "Rack") : "No rack",
  selectedPuck.value ? entityTitle(selectedPuck.value, "Puck") : "No puck",
  selectedWellNo.value ? `Well ${selectedWellNo.value}` : "No well",
  selectedBox.value ? entityTitle(selectedBox.value, "Box") : "Empty well",
]);

const racksForSelectedDewar = computed(() =>
  selectedDewar.value
    ? inventoryStore
        .getRacksForDewar(canonicalId(selectedDewar.value))
        .sort((a, b) => Number(a.dewarPosition ?? a.position ?? 999) - Number(b.dewarPosition ?? b.position ?? 999))
    : []
);
const pucksForSelectedRack = computed(() =>
  selectedRack.value
    ? inventoryStore
        .getPucksForRack(canonicalId(selectedRack.value))
        .sort((a, b) => Number(a.rackPosition ?? a.position ?? 999) - Number(b.rackPosition ?? b.position ?? 999))
    : []
);
const puckSlotsForSelectedRack = computed(() => {
  if (!selectedRack.value) return [];
  const pucks = pucksForSelectedRack.value;
  const baseCapacity = String(selectedRack.value.rackType || "").toLowerCase() === "storage" ? 10 : 6;
  const highestPosition = Math.max(
    0,
    ...pucks.map((puck) => Number(puck.rackPosition ?? puck.position ?? 0))
  );
  const capacity = Math.max(baseCapacity, highestPosition, pucks.length);
  const slots = Array.from({ length: capacity }, (_, index) => ({
    position: index + 1,
    puck: null,
  }));
  const occupied = new Set();
  pucks.forEach((puck, index) => {
    let position = Number(puck.rackPosition ?? puck.position ?? index + 1);
    if (!Number.isFinite(position) || position < 1 || position > capacity || occupied.has(position)) {
      position = slots.find((slot) => !occupied.has(slot.position))?.position || index + 1;
    }
    occupied.add(position);
    const slot = slots[position - 1];
    if (slot) slot.puck = puck;
  });
  return slots;
});
const wellTiles = computed(() => {
  if (!selectedPuck.value) return [];
  const count = wellCount(selectedPuck.value);
  return Array.from({ length: count }, (_, index) => {
    const no = index + 1;
    const box = inventoryStore.getBoxAtWell(canonicalId(selectedPuck.value), no);
    return {
      no,
      box,
      readOnly: box ? isReadOnly("boxes", box) : isReadOnly("pucks", selectedPuck.value),
    };
  });
});
const puckSilhouetteWellRows = computed(() =>
  selectedPuck.value ? miniPuckWellRows(selectedPuck.value) : []
);
const occupiedWellNumbers = computed(() =>
  puckSilhouetteWellRows.value
    .filter((well) => well.box)
    .map((well) => well.no)
);
const wellGridStyle = computed(() => {
  const count = wellTiles.value.length || 12;
  const columns = count <= 6 ? 3 : 4;
  return { gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` };
});
const gridSlots = computed(() => {
  const grids = selectedBox.value ? getGridsForBox(canonicalId(selectedBox.value)) : [];
  const slotsByPosition = new Map([
    { position: 1, label: "Top Left" },
    { position: 2, label: "Top Right" },
    { position: 3, label: "Bottom Left" },
    { position: 4, label: "Bottom Right" },
  ].map((slot) => [slot.position, slot]));

  return gridSlotOrder(selectedBox.value?.gridNumbering).map((position) => {
    const slot = slotsByPosition.get(position);
    return {
      ...slot,
      grid: grids.find((grid) => Number(grid.position) === position) || null,
    };
  });
});
const inspectorTabs = computed(() => [
  {
    id: "box",
    label: "Box",
    icon: "inventory_2",
    disabled: !selectedPuck.value,
    title: selectedPuck.value ? "Manage box settings and moves" : "Select a puck first",
  },
  {
    id: "grids",
    label: "Grids",
    icon: "grid_view",
    disabled: !selectedBox.value,
    title: selectedBox.value ? "Manage grid slots" : "Select or create a box first",
  },
  {
    id: "lamellae",
    label: "Lamellae",
    icon: "biotech",
    disabled: !selectedGrid.value,
    title: selectedGrid.value ? "Manage lamellae for the selected grid" : "Select or create a grid first",
  },
  {
    id: "history",
    label: "History",
    icon: "history",
    disabled: !selectedBox.value,
    title: selectedBox.value ? "Review well activity" : "Select or create a box first",
  },
]);
const moveTargetDewarOptions = computed(() => dewars.value);
const moveTargetRackOptions = computed(() =>
  moveTargetDewarId.value ? getRacksForDewar(moveTargetDewarId.value) : []
);
const moveTargetPuckOptions = computed(() =>
  moveTargetRackId.value ? getPucksForRack(moveTargetRackId.value) : []
);
const moveTargetWellOptions = computed(() => {
  const puck = moveTargetPuckId.value ? getPuck(moveTargetPuckId.value) : null;
  if (!puck) return [];
  const selectedBoxId = canonicalId(selectedBox.value);
  return Array.from({ length: wellCount(puck) }, (_, index) => index + 1).filter((wellNo) => {
    const occupyingBox = getBoxAtWell(canonicalId(puck), wellNo);
    return !occupyingBox || canonicalId(occupyingBox) === selectedBoxId;
  });
});
const canEditMoveTarget = computed(() =>
  Boolean(selectedBox.value && canEdit("boxes", selectedBox.value))
);
const canMoveBox = computed(() => {
  if (!selectedBox.value || !canEdit("boxes", selectedBox.value)) return false;
  if (!moveTargetPuckId.value || !moveTargetWell.value) return false;
  const samePuck = moveTargetPuckId.value === canonicalId(selectedPuck.value);
  const sameWell = Number(moveTargetWell.value) === Number(selectedWellNo.value);
  return !(samePuck && sameWell);
});
const canPasteBox = computed(() =>
  Boolean(copiedBox.value && selectedPuck.value && selectedWellNo.value && !selectedBox.value && canEdit("pucks", selectedPuck.value))
);
const canPasteGrid = computed(() =>
  Boolean(copiedGrid.value && selectedBox.value && !selectedGrid.value && canEdit("boxes", selectedBox.value))
);
const archivedGridOptions = computed(() => inventoryStore.getArchivedGrids());
const canChooseArchivedGrid = computed(() =>
  Boolean(selectedBox.value && !selectedGrid.value && canEdit("boxes", selectedBox.value) && archivedGridOptions.value.length)
);
const canImportArchivedGrid = computed(() =>
  Boolean(archivedGridImportId.value && canChooseArchivedGrid.value)
);

const readOnlyMap = computed(() => {
  const map = new Map();
  const permissions = appStore.state.inventoryPermissions;

  if (permissions && typeof permissions === "object" && !Array.isArray(permissions)) {
    ["dewars", "racks", "pucks", "boxes"].forEach((type) => {
      Object.entries(permissions[type] || {}).forEach(([id, entry]) => {
        if (entry?.readOnly !== undefined) {
          map.set(`${type}:${id}`, !!entry.readOnly);
        }
      });
    });
    return map;
  }

  const data = Array.isArray(appStore.state.dewarData) ? appStore.state.dewarData : [];
  data.forEach((lab) => {
    (lab?.data || []).forEach((dewar) => {
      setReadOnly(map, "dewars", dewar, dewar?.readOnly);
      (dewar?.racks || []).forEach((rack) => {
        setReadOnly(map, "racks", rack, rack?.readOnly);
        (rack?.pucks || []).forEach((puck) => {
          setReadOnly(map, "pucks", puck, puck?.readOnly);
          for (let wellNo = 1; wellNo <= 16; wellNo += 1) {
            const box = puck?.[`well${wellNo}`];
            if (!box || box === "hide") continue;
            setReadOnly(map, "boxes", box, box?.readOnly);
            gridSlotKeys.forEach((slot) => {
              if (box?.[slot]) {
                setReadOnly(map, "grids", box[slot], box?.readOnly);
              }
            });
          }
        });
      });
    });
  });
  return map;
});

const selectedDewarStatus = computed(() => getEntityStatus("dewars", selectedDewar.value));
const selectedRackStatus = computed(() => getEntityStatus("racks", selectedRack.value));
const selectedPuckStatus = computed(() => getEntityStatus("pucks", selectedPuck.value));
const selectedBoxStatus = computed(() => getEntityStatus("boxes", selectedBox.value));
const selectedGridStatus = computed(() => getEntityStatus("grids", selectedGrid.value));

const ENTITY_EDITOR_LABELS = {
  dewars: "Dewar",
  racks: "Rack",
  pucks: "Puck",
};

const entityEditorEntity = computed(() => {
  if (entityEditorMode.value === "dewars") return selectedDewar.value;
  if (entityEditorMode.value === "racks") return selectedRack.value;
  if (entityEditorMode.value === "pucks") return selectedPuck.value;
  return null;
});

const entityEditorEyebrow = computed(() => ENTITY_EDITOR_LABELS[entityEditorMode.value] || "Inventory");
const entityEditorTitle = computed(() => {
  const fallback = ENTITY_EDITOR_LABELS[entityEditorMode.value] || "Inventory";
  return `Edit ${entityTitle(entityEditorEntity.value, fallback)}`;
});
const entityEditorSubtitle = computed(() => {
  if (entityEditorMode.value === "dewars") return "Dewar settings";
  if (entityEditorMode.value === "racks") {
    return selectedDewar.value ? `In ${entityTitle(selectedDewar.value, "Dewar")}` : "Rack settings";
  }
  if (entityEditorMode.value === "pucks") {
    return selectedRack.value ? `In ${entityTitle(selectedRack.value, "Rack")}` : "Puck settings";
  }
  return "";
});
const entityEditorStatus = computed(() => {
  if (entityEditorMode.value === "dewars") return selectedDewarStatus.value;
  if (entityEditorMode.value === "racks") return selectedRackStatus.value;
  if (entityEditorMode.value === "pucks") return selectedPuckStatus.value;
  return { status: "idle", error: null };
});

const gridSlotKeys = ["gridTopLeft", "gridTopRight", "gridBottomLeft", "gridBottomRight"];

watch(dewars, (next) => {
  if (!next.length) {
    selectedDewarId.value = null;
    return;
  }
  if (!selectedDewarId.value || !getDewar(selectedDewarId.value)) {
    selectDewar(canonicalId(next[0]));
  }
}, { immediate: true });

watch(racksForSelectedDewar, (next) => {
  if (!next.length) {
    selectedRackId.value = null;
    return;
  }
  if (!selectedRackId.value || !next.some((rack) => canonicalId(rack) === selectedRackId.value)) {
    selectRack(canonicalId(next[0]));
  }
}, { immediate: true });

watch(pucksForSelectedRack, (next) => {
  if (!next.length) {
    selectedPuckId.value = null;
    return;
  }
  if (!selectedPuckId.value || !next.some((puck) => canonicalId(puck) === selectedPuckId.value)) {
    selectPuck(canonicalId(next[0]));
  }
}, { immediate: true });

watch(selectedDewar, (entity) => {
  Object.assign(dewarDraft, {
    title: entityTitle(entity, ""),
    serialNo: entity?.serialNo || "",
    customId: entity?.customId || "",
    capacity: Number(entity?.capacity || 10),
  });
}, { immediate: true });

watch(selectedRack, (entity) => {
  Object.assign(rackDraft, {
    title: entityTitle(entity, ""),
    serialNo: entity?.serialNo || "",
    dewarPosition: Number(entity?.dewarPosition ?? entity?.position ?? 1),
    rackType: entity?.rackType || "Storage",
  });
}, { immediate: true });

watch(selectedPuck, (entity) => {
  Object.assign(puckDraft, {
    title: entityTitle(entity, ""),
    serialNo: entity?.serialNo || "",
    wellCount: Number(entity?.wellCount || 12),
    color: entity?.color || "chrome",
  });
  selectedWellNo.value = Math.min(selectedWellNo.value || 1, wellCount(entity));
  setDefaultMoveTarget();
}, { immediate: true });

watch(selectedBox, (entity) => {
  Object.assign(boxDraft, {
    title: entityTitle(entity, ""),
    customId: entity?.customId || "",
    gridNumbering: Number(entity?.gridNumbering || 1),
    color: entity?.color || null,
  });
  if (!gridSlots.value.some((slot) => slot.position === selectedGridPosition.value)) {
    selectedGridPosition.value = 1;
  }
  if (!entity && inspectorTab.value !== "box") {
    inspectorTab.value = "box";
  }
  setDefaultMoveTarget();
}, { immediate: true });

watch(selectedGrid, (entity) => {
  Object.assign(gridDraft, {
    name: entityTitle(entity, ""),
    gridCatalogType: entity?.gridCatalogType || "",
    forTomography: !!entity?.forTomography,
  });
  if (!entity && inspectorTab.value === "lamellae") {
    inspectorTab.value = selectedBox.value ? "grids" : "box";
  }
}, { immediate: true });

watch(moveTargetDewarId, (id) => {
  const racks = id ? getRacksForDewar(id) : [];
  if (!racks.some((rack) => canonicalId(rack) === moveTargetRackId.value)) {
    moveTargetRackId.value = racks[0] ? canonicalId(racks[0]) : null;
  }
});

watch(moveTargetRackId, (id) => {
  const pucks = id ? getPucksForRack(id) : [];
  if (!pucks.some((puck) => canonicalId(puck) === moveTargetPuckId.value)) {
    moveTargetPuckId.value = pucks[0] ? canonicalId(pucks[0]) : null;
  }
});

watch(moveTargetPuckId, () => {
  if (!moveTargetWellOptions.value.includes(Number(moveTargetWell.value))) {
    moveTargetWell.value = moveTargetWellOptions.value[0] || null;
  }
});

onMounted(async () => {
  if (!inventoryStore.isInitialized && !providerStore.ydoc) {
    try {
      await inventoryStore.connectActiveLab();
    } catch (error) {
      console.log("failed to connect inventory Yjs session", error);
    }
  }

  await nextTick();
  await refreshLegacySnapshot({ force: !usesLiveInventory.value });
  await applyGridExplorerNavigation();
});

watch(
  () => [
    route.query.navigation,
    usesLiveInventory.value,
    dewars.value.length,
    inventoryStore.racksList.length,
    inventoryStore.pucksList.length,
    inventoryStore.boxesList.length,
    inventoryStore.gridsList.length,
    legacyDewars.value.length,
    legacyRacks.value.length,
    legacyPucks.value.length,
    legacyBoxes.value.length,
    legacyGrids.value.length,
  ],
  () => {
    void applyGridExplorerNavigation();
  }
);

onBeforeUnmount(() => {
  Object.values(autosaveTimers).forEach((timer) => clearTimeout(timer));
});

function canonicalId(value) {
  if (!value) return null;
  if (typeof value === "string") return value;
  return value.id || null;
}

function entityTitle(entity, fallback) {
  if (!entity) return fallback;
  return entity.title || entity.name || entity.customId || entity.serialNo || entity.id || fallback;
}

function setReadOnly(map, type, entity, readOnly) {
  const id = canonicalId(entity);
  if (!id || readOnly === undefined) return;
  map.set(`${type}:${id}`, !!readOnly);
}

function isReadOnly(type, entity) {
  const id = canonicalId(entity);
  if (!id) return false;
  const key = `${type}:${id}`;
  if (readOnlyMap.value.has(key)) {
    return readOnlyMap.value.get(key) === true;
  }

  if (type === "grids" && entity?.boxId && readOnlyMap.value.has(`boxes:${entity.boxId}`)) {
    return readOnlyMap.value.get(`boxes:${entity.boxId}`) === true;
  }

  const hasScopedPermissions =
    appStore.state.inventoryPermissions &&
    typeof appStore.state.inventoryPermissions === "object" &&
    !Array.isArray(appStore.state.inventoryPermissions);
  if (
    hasScopedPermissions &&
    (providerStore.sessionData?.readOnly === true || providerStore.sessionData?.access === "readonly") &&
    ["dewars", "racks", "pucks", "boxes", "grids"].includes(type)
  ) {
    return true;
  }

  return false;
}

function canEdit(type, entity) {
  if (!entity || !appStore.state.activeUser) return false;
  return !isReadOnly(type, entity);
}

function findById(list, id) {
  return list.find((entry) => canonicalId(entry) === id) || null;
}

function getDewar(id) {
  return usesLiveInventory.value ? inventoryStore.getDewar(id) : findById(legacyDewars.value, id);
}

function getRack(id) {
  return usesLiveInventory.value ? inventoryStore.getRack(id) : findById(legacyRacks.value, id);
}

function getPuck(id) {
  return usesLiveInventory.value ? inventoryStore.getPuck(id) : findById(legacyPucks.value, id);
}

function getRacksForDewar(dewarId) {
  if (usesLiveInventory.value) return inventoryStore.getRacksForDewar(dewarId);
  return legacyRacks.value.filter((rack) => canonicalId(rack.dewarId) === dewarId);
}

function getPucksForRack(rackId) {
  if (usesLiveInventory.value) return inventoryStore.getPucksForRack(rackId);
  return legacyPucks.value.filter((puck) => canonicalId(puck.rackId) === rackId);
}

function getBoxesForPuck(puckId) {
  if (usesLiveInventory.value) return inventoryStore.getBoxesForPuck(puckId);
  return legacyBoxes.value.filter((box) => canonicalId(box.puckId) === puckId);
}

function getBoxAtWell(puckId, wellNo) {
  if (usesLiveInventory.value) return inventoryStore.getBoxAtWell(puckId, wellNo);
  return legacyBoxes.value.find(
    (box) => canonicalId(box.puckId) === puckId && Number(box.wellNo) === Number(wellNo)
  ) || null;
}

function getGridsForBox(boxId) {
  if (usesLiveInventory.value) return inventoryStore.getGridsForBox(boxId);
  return legacyGrids.value.filter((grid) => canonicalId(grid.boxId) === boxId);
}

function wellCount(puck) {
  return Math.max(1, Number(puck?.wellCount || 12));
}

function occupiedWellCount(puck) {
  if (!puck) return 0;
  return getBoxesForPuck(canonicalId(puck)).length;
}

function gridsForBoxVisual(box) {
  if (!box) return [];
  return getGridsForBox(canonicalId(box)).filter((grid) => !grid.archived);
}

function gridStatesForBox(box) {
  const grids = gridsForBoxVisual(box);
  const slotCount = Math.max(1, Number(box?.slotCount || grids.length || 4));
  return Array.from({ length: slotCount }, (_, position) =>
    grids.find((grid) => Number(grid.position) === position + 1) || null
  );
}

function miniPuckWellRows(puck) {
  const puckId = canonicalId(puck);
  const count = wellCount(puck);
  return Array.from({ length: count }, (_, index) => {
    const no = index + 1;
    const box = getBoxAtWell(puckId, no);
    const grids = gridsForBoxVisual(box);
    return {
      no,
      box: box || null,
      gridCount: grids.length,
      gridStates: box ? gridStatesForBox(box) : [],
    };
  });
}

function puckColor(puck) {
  const color = puck?.color || "chrome";
  try {
    return appStore.transformPuckColor(color);
  } catch {
    return color === "chrome" ? "#dbe4eb" : color;
  }
}

function boxType(box) {
  return String(box?.boxType || box?.type || "Clipped").toLowerCase();
}

function boxVisualComponent(box) {
  const type = boxType(box);
  if (type.includes("unclipped")) return UnclippedBox;
  if (type.includes("open")) return OpenCupBox;
  return ClippedBox;
}

function boxVisualClass(box) {
  const type = boxType(box);
  if (type.includes("unclipped") || type.includes("open")) return "well-unclipped";
  return "well-clipped";
}

function boxColor(box) {
  const color = box?.color || "default";
  if (color === "default" || color === "chrome") return "#cfd8df";
  try {
    return appStore.transformBoxColor ? appStore.transformBoxColor(color) : color;
  } catch {
    return color;
  }
}

function boxDarkColor(box) {
  const color = boxColor(box);
  return color === "#cfd8df" ? "#6b7280" : color;
}

function boxGridClipState(box) {
  if (!box) return [false, false, false, false];
  const grids = getGridsForBox(canonicalId(box));
  return [1, 2, 3, 4].map((position) => grids.some((grid) => Number(grid.position) === position));
}

function gridNumberingLabel(option) {
  return gridNumberingLabels[Number(option)] || `Setting ${option}`;
}

function setBoxDraftField(field, value) {
  boxDraft[field] = value;
  if (selectedBox.value && canEdit("boxes", selectedBox.value)) {
    queueAutosave("boxes", field);
  }
}

function applyBoxTemplate(id) {
  const template = boxSettingTemplates.value.find((entry) => canonicalId(entry) === id);
  if (!template) return;
  if (template.gridNumbering !== undefined) setBoxDraftField("gridNumbering", Number(template.gridNumbering || 1));
  if (template.color !== undefined) setBoxDraftField("color", template.color || null);
  if (template.title !== undefined && !selectedBox.value) boxDraft.title = template.title || "";
  if (template.customId !== undefined && !selectedBox.value) boxDraft.customId = template.customId || "";
}

function selectDewar(id) {
  selectedDewarId.value = id;
  const firstRack = id ? getRacksForDewar(id)[0] : null;
  selectedRackId.value = firstRack ? canonicalId(firstRack) : null;
  const firstPuck = firstRack ? getPucksForRack(canonicalId(firstRack))[0] : null;
  selectedPuckId.value = firstPuck ? canonicalId(firstPuck) : null;
  selectedWellNo.value = 1;
  selectedGridPosition.value = 1;
}

function selectRack(id) {
  selectedRackId.value = id;
  const firstPuck = id ? getPucksForRack(id)[0] : null;
  selectedPuckId.value = firstPuck ? canonicalId(firstPuck) : null;
  selectedWellNo.value = 1;
  selectedGridPosition.value = 1;
}

function selectPuck(id) {
  selectedPuckId.value = id;
  selectedWellNo.value = 1;
  selectedGridPosition.value = 1;
}

function openDewarEditor(id = selectedDewarId.value) {
  if (id && id !== selectedDewarId.value) {
    selectDewar(id);
  }
  if (selectedDewar.value || id) {
    entityEditorMode.value = "dewars";
  }
}

function openRackEditor(id = selectedRackId.value) {
  if (id && id !== selectedRackId.value) {
    selectRack(id);
  }
  if (selectedRack.value || id) {
    entityEditorMode.value = "racks";
  }
}

function openPuckEditor(id = selectedPuckId.value) {
  if (id && id !== selectedPuckId.value) {
    selectPuck(id);
  }
  if (selectedPuck.value || id) {
    entityEditorMode.value = "pucks";
  }
}

async function flushEntityEditorAutosave() {
  if (!entityEditorMode.value) return;
  await saveDraft(entityEditorMode.value);
}

async function closeEntityEditor() {
  await flushEntityEditorAutosave();
  entityEditorMode.value = null;
}

function isDewarCollapsed(id) {
  return collapsedDewarIds.value.has(id);
}

function toggleDewar(id) {
  if (!id) return;
  const next = new Set(collapsedDewarIds.value);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  collapsedDewarIds.value = next;
  if (typeof window !== "undefined") {
    window.localStorage.setItem(COLLAPSED_DEWAR_IDS_KEY, JSON.stringify([...next]));
  }
}

function selectWell(wellNo) {
  selectedWellNo.value = wellNo;
  selectedGridPosition.value = 1;
}

function wellOrbStyle(wellNo, count) {
  if (count <= 1) {
    return { left: "50%", top: "50%" };
  }
  const ring = count <= 6 ? 38 : 39;
  const angle = -90 + ((wellNo - 1) * 360) / count;
  const radians = (angle * Math.PI) / 180;
  return {
    left: `${50 + Math.cos(radians) * ring}%`,
    top: `${50 + Math.sin(radians) * ring}%`,
  };
}

function setDefaultMoveTarget() {
  moveTargetDewarId.value = selectedDewar.value ? canonicalId(selectedDewar.value) : null;
  moveTargetRackId.value = selectedRack.value ? canonicalId(selectedRack.value) : null;
  moveTargetPuckId.value = selectedPuck.value ? canonicalId(selectedPuck.value) : null;
  moveTargetWell.value = selectedWellNo.value || null;
}

function nextOpenPosition(items, field, max) {
  const used = new Set(items.map((item) => Number(item?.[field] ?? item?.position)).filter(Boolean));
  for (let pos = 1; pos <= max; pos += 1) {
    if (!used.has(pos)) return pos;
  }
  return max + 1;
}

async function createDewar() {
  try {
    const id = await inventoryStore.createDewar({
      title: "New Dewar",
      capacity: 10,
    });
    await refreshAfterScopedFallback();
    if (id) selectDewar(id);
  } catch (error) {
    window.alert(error?.message || "Failed to create dewar");
  }
}

async function createRack() {
  if (!selectedDewar.value || !canEdit("dewars", selectedDewar.value)) return;
  try {
    const position = nextOpenPosition(racksForSelectedDewar.value, "dewarPosition", Number(selectedDewar.value.capacity || 10));
    const id = await inventoryStore.createRack({
      title: `Rack ${position}`,
      dewarId: canonicalId(selectedDewar.value),
      dewarPosition: position,
      position,
      rackType: "Storage",
    });
    await refreshAfterScopedFallback();
    if (id) selectRack(id);
  } catch (error) {
    window.alert(error?.message || "Failed to create rack");
  }
}

async function createPuck() {
  if (!selectedRack.value || !canEdit("racks", selectedRack.value)) return;
  try {
    const position = nextOpenPosition(pucksForSelectedRack.value, "rackPosition", 10);
    const id = await inventoryStore.createPuck({
      title: `Puck ${position}`,
      rackId: canonicalId(selectedRack.value),
      rackPosition: position,
      position,
      wellCount: 12,
      color: "chrome",
    });
    await refreshAfterScopedFallback();
    if (id) selectPuck(id);
  } catch (error) {
    window.alert(error?.message || "Failed to create puck");
  }
}

async function createBox() {
  if (!selectedPuck.value || !selectedWellNo.value || !canEdit("pucks", selectedPuck.value)) return;
  try {
    await inventoryStore.createBox({
      title: boxDraft.title || `Box ${selectedWellNo.value}`,
      customId: boxDraft.customId || undefined,
      puckId: canonicalId(selectedPuck.value),
      wellNo: selectedWellNo.value,
      gridNumbering: Number(boxDraft.gridNumbering || 1),
      color: boxDraft.color || null,
    });
    await refreshAfterScopedFallback();
  } catch (error) {
    window.alert(error?.message || "Failed to create box");
  }
}

async function createGrid() {
  if (!selectedBox.value || !canEdit("boxes", selectedBox.value)) return;
  try {
    await inventoryStore.createGrid({
      boxId: canonicalId(selectedBox.value),
      position: selectedGridPosition.value,
      name: `Grid ${selectedGridPosition.value}`,
    });
    await refreshAfterScopedFallback();
  } catch (error) {
    window.alert(error?.message || "Failed to create grid");
  }
}

function copySelectedBox() {
  if (!selectedBox.value) return;
  copiedBox.value = {
    title: boxDraft.title || entityTitle(selectedBox.value, ""),
    customId: boxDraft.customId || selectedBox.value.customId || "",
    gridNumbering: Number(boxDraft.gridNumbering || selectedBox.value.gridNumbering || 1),
    color: boxDraft.color || selectedBox.value.color || null,
    boxType: selectedBox.value.boxType || null,
    description: selectedBox.value.description || "",
    projectId: selectedBox.value.projectId || selectedBox.value.project || null,
  };
}

async function pasteCopiedBox() {
  if (!canPasteBox.value) return;
  try {
    const source = copiedBox.value;
    await inventoryStore.createBox({
      ...source,
      title: source.title ? `${source.title} Copy` : `Box ${selectedWellNo.value}`,
      puckId: canonicalId(selectedPuck.value),
      wellNo: selectedWellNo.value,
    });
    inspectorTab.value = "box";
    await refreshAfterScopedFallback();
  } catch (error) {
    window.alert(error?.message || "Failed to paste box");
  }
}

async function moveSelectedBox() {
  if (!canMoveBox.value) return;
  try {
    const targetPuckId = moveTargetPuckId.value;
    const targetWellNo = Number(moveTargetWell.value);
    await inventoryStore.moveBoxToWell(canonicalId(selectedBox.value), targetPuckId, targetWellNo);
    if (moveFollowTarget.value) {
      const targetPuck = getPuck(targetPuckId);
      const targetRack = targetPuck ? getRack(canonicalId(targetPuck.rackId)) : null;
      const targetDewar = targetRack ? getDewar(canonicalId(targetRack.dewarId)) : null;
      selectedDewarId.value = targetDewar ? canonicalId(targetDewar) : moveTargetDewarId.value;
      selectedRackId.value = targetRack ? canonicalId(targetRack) : moveTargetRackId.value;
      selectedPuckId.value = targetPuckId;
      selectedWellNo.value = targetWellNo;
    }
    await refreshAfterScopedFallback();
  } catch (error) {
    window.alert(error?.message || "Failed to move box");
  }
}

async function handlePuckSilhouetteMove(payload) {
  if (!selectedPuck.value || !payload?.boxId) return;
  const sourceBox = getBoxAtWell(canonicalId(selectedPuck.value), payload.sourceWellNo);
  if (!sourceBox || !canEdit("boxes", sourceBox)) return;
  try {
    await inventoryStore.moveBoxToWell(
      payload.boxId,
      payload.targetPuckId || canonicalId(selectedPuck.value),
      Number(payload.targetWellNo)
    );
    selectedWellNo.value = Number(payload.targetWellNo);
    selectedGridPosition.value = 1;
    await refreshAfterScopedFallback();
  } catch (error) {
    window.alert(error?.message || "Failed to move box");
  }
}

function copySelectedGrid() {
  if (!selectedGrid.value) return;
  copiedGrid.value = {
    name: gridDraft.name || entityTitle(selectedGrid.value, ""),
    description: selectedGrid.value.description || "",
    gridType: Array.isArray(selectedGrid.value.gridType) ? [...selectedGrid.value.gridType] : [],
    gridCatalogType: gridDraft.gridCatalogType || selectedGrid.value.gridCatalogType || null,
    forTomography: !!gridDraft.forTomography,
    imageurl: selectedGrid.value.imageurl || null,
  };
}

async function pasteCopiedGrid() {
  if (!canPasteGrid.value) return;
  try {
    const source = copiedGrid.value;
    await inventoryStore.createGrid({
      ...source,
      name: source.name ? `${source.name} Copy` : `Grid ${selectedGridPosition.value}`,
      boxId: canonicalId(selectedBox.value),
      position: selectedGridPosition.value,
    });
    await refreshAfterScopedFallback();
  } catch (error) {
    window.alert(error?.message || "Failed to paste grid");
  }
}

async function importArchivedGrid() {
  if (!canImportArchivedGrid.value) return;
  try {
    await inventoryStore.updateGrid(archivedGridImportId.value, {
      archived: false,
      boxId: canonicalId(selectedBox.value),
      position: selectedGridPosition.value,
    });
    archivedGridImportId.value = "";
    await refreshAfterScopedFallback();
  } catch (error) {
    window.alert(error?.message || "Failed to import archived grid");
  }
}

async function createLamellaForSelectedGrid() {
  if (!selectedGrid.value || !canEdit("grids", selectedGrid.value)) return;
  try {
    await inventoryStore.createLamella({
      gridId: canonicalId(selectedGrid.value),
      name: `Lamella ${selectedLamellae.value.length + 1}`,
      status: "pending",
    });
    await refreshAfterScopedFallback();
  } catch (error) {
    window.alert(error?.message || "Failed to create lamella");
  }
}

async function updateLamellaStatus(id, status) {
  if (!id || !selectedGrid.value || !canEdit("grids", selectedGrid.value)) return;
  try {
    await inventoryStore.updateLamella(id, { status });
    await refreshAfterScopedFallback();
  } catch (error) {
    window.alert(error?.message || "Failed to update lamella");
  }
}

async function deleteSelectedPuck() {
  if (!selectedPuck.value || !canEdit("pucks", selectedPuck.value)) return;
  if (!window.confirm("Delete this puck and its boxes?")) return;
  try {
    await inventoryStore.deletePuck(canonicalId(selectedPuck.value));
    await refreshAfterScopedFallback();
  } catch (error) {
    window.alert(error?.message || "Failed to delete puck");
  }
}

async function deleteSelectedBox() {
  if (!selectedBox.value || !canEdit("boxes", selectedBox.value)) return;
  if (!window.confirm("Delete this box and archive its grids?")) return;
  try {
    await inventoryStore.deleteBox(canonicalId(selectedBox.value));
    await refreshAfterScopedFallback();
  } catch (error) {
    window.alert(error?.message || "Failed to delete box");
  }
}

async function deleteSelectedGrid() {
  if (!selectedGrid.value || !canEdit("grids", selectedGrid.value)) return;
  if (!window.confirm("Archive this grid?")) return;
  try {
    await inventoryStore.deleteGrid(canonicalId(selectedGrid.value));
    await refreshAfterScopedFallback();
  } catch (error) {
    window.alert(error?.message || "Failed to archive grid");
  }
}

function autosaveKey(type) {
  const entity = {
    dewars: selectedDewar.value,
    racks: selectedRack.value,
    pucks: selectedPuck.value,
    boxes: selectedBox.value,
    grids: selectedGrid.value,
  }[type];
  return `${type}:${canonicalId(entity) || "create"}`;
}

function normalizeAutosaveFields(type, fields) {
  if (Array.isArray(fields)) return fields;
  if (typeof fields === "string") return [fields];
  return Object.keys(AUTOSAVE_FIELD_BUILDERS[type] || {});
}

function buildAutosavePayload(type, fields) {
  const builders = AUTOSAVE_FIELD_BUILDERS[type] || {};
  return normalizeAutosaveFields(type, fields).reduce((payload, field) => {
    const buildPayload = builders[field];
    if (buildPayload) Object.assign(payload, buildPayload());
    return payload;
  }, {});
}

function recordAutosaveState(key, type, fields) {
  const normalizedFields = normalizeAutosaveFields(type, fields);
  if (!autosaveDirtyFields[key]) autosaveDirtyFields[key] = new Set();
  normalizedFields.forEach((field) => autosaveDirtyFields[key].add(field));
  autosaveQueuedPayloads[key] = {
    ...(autosaveQueuedPayloads[key] || {}),
    ...buildAutosavePayload(type, normalizedFields),
  };
}

function getAutosaveEntity(type, id) {
  if (!id || id === "create") return null;
  if (type === "dewars") return getDewar(id);
  if (type === "racks") return getRack(id);
  if (type === "pucks") return getPuck(id);
  if (type === "boxes") {
    return usesLiveInventory.value ? inventoryStore.getBox(id) : findById(legacyBoxes.value, id);
  }
  if (type === "grids") {
    return usesLiveInventory.value ? inventoryStore.getGrid(id) : findById(legacyGrids.value, id);
  }
  return null;
}

function autosaveIdFromKey(type, key) {
  const prefix = `${type}:`;
  return key?.startsWith(prefix) ? key.slice(prefix.length) : null;
}

function clearSavedAutosaveState(type, key, savedFields, savedPayload) {
  const dirtyFields = autosaveDirtyFields[key];
  const queuedPayload = autosaveQueuedPayloads[key];
  if (!dirtyFields || !queuedPayload) {
    delete autosaveDirtyFields[key];
    delete autosaveQueuedPayloads[key];
    return;
  }

  savedFields.forEach((field) => {
    const payloadKeys = AUTOSAVE_FIELD_PAYLOAD_KEYS[type]?.[field] || [];
    const hasNewerQueuedValue = payloadKeys.some(
      (payloadKey) =>
        Object.prototype.hasOwnProperty.call(queuedPayload, payloadKey) &&
        queuedPayload[payloadKey] !== savedPayload[payloadKey]
    );
    if (hasNewerQueuedValue) return;
    dirtyFields.delete(field);
    payloadKeys.forEach((payloadKey) => {
      delete queuedPayload[payloadKey];
    });
  });

  if (!dirtyFields.size) delete autosaveDirtyFields[key];
  if (!Object.keys(queuedPayload).length) delete autosaveQueuedPayloads[key];
}

function queueAutosave(type, fields = null) {
  const key = autosaveKey(type);
  recordAutosaveState(key, type, fields);
  localStatuses[key] = { status: "pending", error: null };
  clearTimeout(autosaveTimers[key]);
  autosaveTimers[key] = setTimeout(() => {
    saveDraft(type, key);
  }, 650);
}

async function saveDraft(type, queuedKey = autosaveKey(type)) {
  const id = autosaveIdFromKey(type, queuedKey);
  const entity = getAutosaveEntity(type, id);
  if (!entity || !canEdit(type, entity)) return;
  const key = queuedKey;
  const dirtyFields = Array.from(autosaveDirtyFields[key] || []);
  const queuedPayload = autosaveQueuedPayloads[key];
  const payload = queuedPayload
    ? { ...queuedPayload }
    : buildAutosavePayload(type, dirtyFields.length ? dirtyFields : null);

  if (!Object.keys(payload).length) return;
  localStatuses[key] = { status: "saving", error: null };

  try {
    if (type === "dewars") {
      await inventoryStore.updateDewar(id, payload);
    } else if (type === "racks") {
      await inventoryStore.updateRack(id, payload);
    } else if (type === "pucks") {
      await inventoryStore.updatePuck(id, payload);
    } else if (type === "boxes") {
      await inventoryStore.updateBox(id, payload);
    } else if (type === "grids") {
      await inventoryStore.updateGrid(id, payload);
    }
    await refreshAfterScopedFallback();
    clearSavedAutosaveState(type, key, dirtyFields, payload);
    localStatuses[key] = { status: "saved", error: null };
    setTimeout(() => {
      if (localStatuses[key]?.status === "saved") {
        localStatuses[key] = { status: "idle", error: null };
      }
    }, 1200);
  } catch (error) {
    localStatuses[key] = { status: "error", error: error?.message || "Autosave failed" };
  }
}

function getEntityStatus(type, entity) {
  const id = canonicalId(entity);
  if (!id) return { status: "idle", error: null };
  const key = `${type}:${id}`;
  const local = localStatuses[key];
  if (local?.status && local.status !== "idle") {
    return local;
  }
  return inventoryStore.getMutationStatus(type, id);
}

function statusLabel(status) {
  if (status?.status === "pending") return "Pending";
  if (status?.status === "saving") return "Saving";
  if (status?.status === "saved") return "Saved";
  if (status?.status === "error") return "Error";
  return "Idle";
}

function handleSearch(serial, highlightbox) {
  const normalized = String(serial || "").trim().toUpperCase();
  if (!normalized) return;
  const puckSource = usesLiveInventory.value ? inventoryStore.pucksList : legacyPucks.value;
  const puck = puckSource.find((entry) =>
    String(entry.serialNo || entry.customId || entityTitle(entry, "")).trim().toUpperCase() === normalized
  );
  if (!puck) return;
  const rack = getRack(canonicalId(puck.rackId));
  if (rack) {
    const dewarId = canonicalId(rack.dewarId);
    if (dewarId) selectedDewarId.value = dewarId;
    selectedRackId.value = canonicalId(rack);
  }
  selectedPuckId.value = canonicalId(puck);
  selectedWellNo.value = Number(highlightbox || 1);
  selectedGridPosition.value = 1;
}

function parseGridExplorerNavigation(raw) {
  const value = Array.isArray(raw) ? raw[0] : raw;
  if (!value || typeof value !== "string") return null;
  try {
    const parsed = JSON.parse(value);
    if (!parsed || typeof parsed !== "object") return null;
    return parsed;
  } catch {
    return null;
  }
}

function getFirstWellForPuck(puckId) {
  const firstBox = getBoxesForPuck(puckId)
    .sort((a, b) => Number(a.wellNo || 999) - Number(b.wellNo || 999))[0];
  return Number(firstBox?.wellNo || 1);
}

function findBoxForNavigation(navigation, grid) {
  const explicitBoxId = canonicalId(navigation?.boxId);
  if (explicitBoxId) {
    const explicitBox = getAutosaveEntity("boxes", explicitBoxId);
    if (explicitBox) return explicitBox;
  }

  const gridBoxId = canonicalId(grid?.boxId || grid?.box);
  if (gridBoxId) {
    const gridBox = getAutosaveEntity("boxes", gridBoxId);
    if (gridBox) return gridBox;
  }

  const puckId = canonicalId(navigation?.puckId || navigation?.puck);
  const wellNo = Number(navigation?.well || navigation?.highlightbox || 0);
  if (puckId && wellNo) return getBoxAtWell(puckId, wellNo);

  return null;
}

async function applyGridExplorerNavigation() {
  const raw = Array.isArray(route.query.navigation) ? route.query.navigation[0] : route.query.navigation;
  if (!raw || raw === lastAppliedNavigation.value) return;

  const navigation = parseGridExplorerNavigation(raw);
  if (!navigation) return;

  let grid = null;
  let box = null;
  let puck = null;

  const gridId = canonicalId(navigation.gridId || navigation.grid);
  if (gridId) {
    grid = getAutosaveEntity("grids", gridId);
  }

  box = findBoxForNavigation(navigation, grid);

  const puckId = canonicalId(navigation.puckId || box?.puckId);
  if (puckId) {
    puck = getPuck(puckId);
  }

  if (!puck && navigation.puck) {
    const serial = String(navigation.puck).trim().toUpperCase();
    const puckSource = usesLiveInventory.value ? inventoryStore.pucksList : legacyPucks.value;
    puck = puckSource.find((entry) =>
      String(entry.serialNo || entry.customId || entityTitle(entry, "")).trim().toUpperCase() === serial
    ) || null;
  }

  if (!box && puck) {
    const wellNo = Number(navigation.well || navigation.highlightbox || 0);
    if (wellNo) box = getBoxAtWell(canonicalId(puck), wellNo);
  }

  if (!box && grid) {
    const gridBoxId = canonicalId(grid.boxId || grid.box);
    if (gridBoxId) box = getAutosaveEntity("boxes", gridBoxId);
  }

  if (!puck && box) {
    puck = getPuck(canonicalId(box.puckId));
  }

  if (!puck) return;

  const rack = getRack(canonicalId(puck.rackId));
  const dewar = getDewar(canonicalId(rack?.dewarId));
  if (!rack || !dewar) return;

  const dewarId = canonicalId(dewar);
  selectedDewarId.value = dewarId;
  selectedRackId.value = canonicalId(rack);
  selectedPuckId.value = canonicalId(puck);
  selectedWellNo.value = Number(box?.wellNo || navigation.well || navigation.highlightbox || getFirstWellForPuck(canonicalId(puck))) || 1;
  selectedGridPosition.value = Number(grid?.position || navigation.gridPosition || 1) || 1;
  inspectorTab.value = "grids";
  if (dewarId && collapsedDewarIds.value.has(dewarId)) {
    const next = new Set(collapsedDewarIds.value);
    next.delete(dewarId);
    collapsedDewarIds.value = next;
    if (typeof window !== "undefined") {
      window.localStorage.setItem(COLLAPSED_DEWAR_IDS_KEY, JSON.stringify([...next]));
    }
  }

  lastAppliedNavigation.value = raw;
  const nextQuery = { ...route.query };
  delete nextQuery.navigation;
  await router.replace({ query: nextQuery }).catch(() => undefined);
}

async function refreshLegacySnapshot({ force = false } = {}) {
  if (snapshotLoading.value) return;
  if (!force && Array.isArray(appStore.state.dewarData) && appStore.state.dewarData.length) {
    try {
      appStore.commit("updateDewarData", { value: appStore.state.dewarData });
    } catch {
      // Legacy permission data is a fallback; live Yjs can still render without it.
    }
    return;
  }

  snapshotLoading.value = true;
  try {
    const cacheBust = `mp=${Date.now()}`;
    const [inventoryResponse, permissionsResponse] = await Promise.all([
      fetch(`${appStore.state.hostname}/api/v1/dewars?${cacheBust}`, {
        credentials: "include",
        method: "GET",
        cache: "no-store",
        headers: { "content-type": "application/json" },
      }),
      fetch(`${appStore.state.hostname}/api/v1/dewars/permissions?${cacheBust}`, {
        credentials: "include",
        method: "GET",
        cache: "no-store",
        headers: { "content-type": "application/json" },
      }),
    ]);
    const [inventoryPayload, permissionsPayload] = await Promise.all([
      inventoryResponse.json(),
      permissionsResponse.json(),
    ]);
    if (!inventoryResponse.ok || inventoryPayload.status === "fail" || inventoryPayload.status === "error") {
      throw new Error(inventoryPayload.message || "Failed to load inventory");
    }
    if (!permissionsResponse.ok || permissionsPayload.status === "fail" || permissionsPayload.status === "error") {
      throw new Error(permissionsPayload.message || "Failed to load inventory permissions");
    }
    appStore.commit("updateDewarData", {
      value: inventoryPayload.data.data,
      permissions: permissionsPayload.data.data,
    });
  } catch (error) {
    if (!usesLiveInventory.value) {
      console.log("failed to load filtered inventory snapshot", error);
    }
  } finally {
    snapshotLoading.value = false;
  }
}

async function refreshAfterScopedFallback() {
  if (!usesLiveInventory.value) {
    await refreshLegacySnapshot({ force: true });
  }
}

async function refreshInventory() {
  let connectError = null;
  try {
    await inventoryStore.connectActiveLab();
  } catch (error) {
    connectError = error;
  }

  await refreshLegacySnapshot({ force: !usesLiveInventory.value });
  if (connectError && !usesLiveInventory.value && !appStore.state.dewarData?.length) {
    window.alert(connectError?.message || "Failed to refresh inventory");
  }
}
</script>

<style scoped>
.mp-redesign {
  min-height: calc(100vh - 57px);
  background: #fff;
  color: #111827;
  padding: 0;
  font-family: Montserrat, Inter, system-ui, sans-serif;
}

.mp-redesign,
.mp-redesign *,
.mp-redesign *::before,
.mp-redesign *::after {
  box-sizing: border-box;
}

.mp-redesign__toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: nowrap;
  width: 100%;
  margin: 0;
  padding: 10px 12px;
  border-bottom: 1px solid #d8e0ea;
  background: #f8fbff;
}

.mp-redesign__search {
  flex: 1 1 520px;
  min-width: 0;
}

.mp-redesign__search :deep(#searchbarheight) {
  position: static;
  top: auto !important;
  z-index: auto !important;
  padding-bottom: 0;
  background: transparent;
}

.mp-redesign__search :deep(#searchbarheight > div.bg-sky-900) {
  width: 100%;
  height: 40px;
  min-height: 40px;
  border: 1px solid #9fb6d3;
  border-radius: 6px;
  background: #ffffff;
  overflow: hidden;
}

.mp-redesign__search :deep(#searchbarforloggedin) {
  flex: 1 1 auto;
  height: 100%;
  min-width: 0;
  margin: 0;
  border: 0;
  background: transparent;
  color: #172033;
  font-size: 1rem;
  padding: 0 12px;
  outline: none;
}

.mp-redesign__search :deep(#searchbarforloggedin::placeholder) {
  color: #8b98a8;
}

.mp-redesign__search :deep(#searchbarheight > div.bg-sky-900 > div:last-child) {
  flex: 0 0 46px;
  display: grid;
  place-items: center;
}

.mp-redesign__search :deep(.material-symbols-outlined) {
  color: #64748b;
}

.mp-redesign__toolbar-actions,
.mp-redesign__stage-actions,
.mp-redesign__inspector-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: nowrap;
}

.mp-redesign__toolbar-actions > .mp-redesign__icon-button,
.mp-redesign__toolbar-actions > .mp-redesign__button {
  display: none;
}

.mp-redesign__path-strip {
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 0 1 min(520px, 38vw);
  min-width: 0;
  max-width: min(520px, 38vw);
  overflow: hidden;
  border: 1px solid #d8e0ea;
  border-radius: 8px;
  background: #ffffff;
  padding: 6px 8px;
}

.mp-redesign__path-strip .material-symbols-outlined {
  flex: 0 0 auto;
  color: #365f91;
  font-size: 18px;
}

.mp-redesign__path-strip span:not(.material-symbols-outlined) {
  flex: 0 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  border: 1px solid #c9d7ec;
  border-radius: 6px;
  background: #f6f9fe;
  color: #28527f;
  padding: 4px 7px;
  font-size: 0.72rem;
  font-weight: 800;
}

.mp-redesign__layout {
  width: 100%;
  margin: 0;
  display: grid;
  grid-template-columns: minmax(220px, 260px) minmax(0, 1fr) minmax(300px, 360px);
  gap: 0;
  align-items: start;
}

.mp-redesign__rail,
.mp-redesign__workbench,
.mp-redesign__inspector {
  min-width: 0;
}

.mp-redesign__rail,
.mp-redesign__stage,
.mp-redesign__detail-panel {
  border: 1px solid #d8dee9;
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}

.mp-redesign__rail {
  position: sticky;
  top: 0;
  align-self: start;
  min-height: calc(100vh - 128px);
  border-width: 0 1px 0 0;
  border-radius: 0;
  padding: 10px;
  display: grid;
  align-content: start;
  gap: 12px;
  box-shadow: none;
}

.mp-redesign__workbench {
  display: grid;
  gap: 12px;
  padding: 12px;
}

.mp-redesign__inspector {
  position: sticky;
  top: 0;
  align-self: start;
  min-height: calc(100vh - 128px);
  border-left: 1px solid #d8e0ea;
  background: #fbfcfe;
}

.mp-redesign__rail-header {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: start;
  justify-items: stretch;
  gap: 10px;
  border-bottom: 1px solid #e2e8f0;
  padding-bottom: 10px;
}

.mp-redesign__rail-header h2 {
  margin: 0;
  color: #111827;
  font-size: 1rem;
  font-weight: 800;
}

.mp-redesign__rail-actions {
  grid-column: 1 / -1;
  display: grid;
  grid-template-columns: auto auto;
  align-items: center;
  justify-content: start;
  gap: 6px;
  width: 100%;
}

.mp-redesign__count-chip {
  flex: 0 0 auto;
  padding: 5px 8px;
  border: 1px solid #d6dde7;
  border-radius: 999px;
  background: #ffffff;
  color: #526173;
  font-size: 0.68rem;
  font-weight: 800;
}

.mp-redesign__mini-command {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  min-height: 28px;
  border: 1px solid #bfdbfe;
  border-radius: 7px;
  background: #ffffff;
  color: #184f88;
  padding: 0 8px;
  font: inherit;
  font-size: 0.74rem;
  font-weight: 900;
  cursor: pointer;
}

.mp-redesign__mini-command:hover {
  border-color: #93c5fd;
  background: #eff8ff;
}

.mp-redesign__mini-command:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.mp-redesign__mini-command .material-symbols-outlined {
  font-size: 17px;
}

.mp-redesign__icon-button--danger {
  border-color: #fecaca;
  color: #b91c1c;
}

.mp-redesign__rail-section {
  min-width: 0;
}

.mp-redesign__tree-list {
  min-width: 0;
  overflow-x: hidden;
  overflow-y: auto;
}

.mp-redesign__tree-group + .mp-redesign__tree-group {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #e8edf3;
}

.mp-redesign__child-list {
  margin: 6px 0 0 14px;
  padding-left: 9px;
  border-left: 1px solid #dbe3ef;
  max-width: calc(100% - 14px);
  min-width: 0;
}

.mp-redesign__tree-row-shell {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  align-items: center;
  gap: 5px;
  max-width: 100%;
  min-width: 0;
}

.mp-redesign__dewar-shell {
  grid-template-columns: 24px minmax(0, 1fr);
}

.mp-redesign__tree-row-shell + .mp-redesign__tree-row-shell,
.mp-redesign__rack-node + .mp-redesign__rack-node {
  margin-top: 3px;
}

.mp-redesign__tree-row {
  display: grid;
  grid-template-columns: 28px minmax(0, 1fr) auto;
  align-items: center;
  gap: 8px;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  min-height: 42px;
  border: 1px solid transparent;
  border-radius: 7px;
  background: transparent;
  color: #172033;
  font: inherit;
  text-align: left;
  cursor: pointer;
}

.mp-redesign__tree-row:hover {
  border-color: #d6dde7;
  background: #ffffff;
}

.mp-redesign__tree-row-shell.active > .mp-redesign__tree-row,
.mp-redesign__tree-row-shell.active > .mp-redesign__collapse-toggle {
  border-color: #88c4e8;
  background: #edf8ff;
}

.mp-redesign__tree-row-shell.active > .mp-redesign__tree-row {
  box-shadow: inset 3px 0 0 #0ea5e9;
}

.mp-redesign__dewar-row {
  padding: 7px 8px;
}

.mp-redesign__child-row,
.mp-redesign__puck-row {
  min-height: 38px;
  padding: 6px 8px;
}

.mp-redesign__puck-row {
  grid-template-columns: 20px minmax(0, 1fr) auto;
  margin-left: 10px;
  width: calc(100% - 10px);
}

.mp-redesign__collapse-toggle,
.mp-redesign__row-actions button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid transparent;
  border-radius: 6px;
  background: transparent;
  color: #526173;
  font: inherit;
  font-size: 0.68rem;
  font-weight: 900;
  cursor: pointer;
}

.mp-redesign__collapse-toggle {
  width: 24px;
  height: 34px;
  padding: 0;
}

.mp-redesign__collapse-toggle:hover,
.mp-redesign__row-actions button:hover {
  border-color: #cbd5e1;
  background: #ffffff;
}

.mp-redesign__row-actions {
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  grid-column: 2 / -1;
  padding-left: 2px;
}

.mp-redesign__child-shell .mp-redesign__row-actions,
.mp-redesign__puck-shell-row .mp-redesign__row-actions {
  grid-column: 1 / -1;
  padding-left: 8px;
}

.mp-redesign__row-actions button {
  height: 28px;
  gap: 3px;
  padding: 0 6px;
  white-space: nowrap;
}

.mp-redesign__row-actions--compact button {
  padding: 0 5px;
}

.mp-redesign__row-actions button:disabled {
  cursor: not-allowed;
  opacity: 0.45;
}

.mp-redesign__row-actions .material-symbols-outlined {
  font-size: 15px;
}

.mp-redesign__asset-badge {
  display: grid;
  place-items: center;
  width: 28px;
  height: 28px;
  border: 1px solid #d6dde7;
  border-radius: 7px;
  background: #ffffff;
}

.mp-redesign__asset-badge img {
  width: 22px;
  height: 22px;
  object-fit: contain;
}

.mp-redesign__row-copy {
  min-width: 0;
}

.mp-redesign__row-copy strong,
.mp-redesign__row-copy small {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mp-redesign__row-copy strong {
  color: #172033;
  font-size: 0.78rem;
  font-weight: 900;
}

.mp-redesign__row-copy small {
  margin-top: 2px;
  color: #64748b;
  font-size: 0.68rem;
  font-weight: 800;
}

.mp-redesign__puck-dot {
  width: 12px;
  height: 12px;
  margin-left: 4px;
  border: 2px solid #ffffff;
  border-radius: 999px;
  box-shadow: 0 0 0 1px #b7c2cf;
}

.mp-redesign__rack-stage,
.mp-redesign__stage,
.mp-redesign__detail-panel {
  border: 1px solid #d8dee9;
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}

.mp-redesign__rack-stage {
  overflow: hidden;
}

.mp-redesign__rack-stage-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  border-bottom: 1px solid #e2e8f0;
}

.mp-redesign__rack-stage-header h2 {
  margin: 0;
  color: #172033;
  font-size: 1rem;
  font-weight: 700;
}

.mp-redesign__rack-stage-header strong {
  color: #334155;
  font-size: 0.84rem;
  font-weight: 900;
}

.mp-redesign__puck-strip {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(132px, 1fr));
  gap: 10px;
  max-width: 100%;
  min-width: 0;
  padding: 12px;
}

.mp-redesign__puck-strip--selected {
  grid-template-columns: minmax(160px, 1.35fr) repeat(auto-fit, minmax(54px, 0.5fr));
  align-items: stretch;
}

.mp-redesign__puck-card {
  display: grid;
  grid-template-rows: 126px auto auto;
  justify-items: center;
  align-items: center;
  gap: 5px;
  min-height: 166px;
  border: 1px solid #d8dee9;
  border-radius: 8px;
  background: #ffffff;
  color: #172033;
  padding: 10px 8px;
  text-align: center;
}

.mp-redesign__puck-strip--selected .mp-redesign__puck-card:not(.mp-redesign__puck-card--active) {
  grid-template-rows: 54px auto auto;
  min-height: 86px;
  gap: 3px;
  padding: 7px 4px;
}

.mp-redesign__puck-card--active {
  border-color: #2ea8ff;
  background: #eff8ff;
  box-shadow: inset 0 0 0 1px #2ea8ff;
}

.mp-redesign__puck-card--empty {
  border-style: dashed;
  background: #fbfcfe;
  color: #94a3b8;
  cursor: default;
}

.mp-redesign__puck-card-art {
  display: grid;
  place-items: center;
  width: 126px;
  height: 134px;
  overflow: hidden;
}

.mp-redesign__puck-strip--selected .mp-redesign__puck-card:not(.mp-redesign__puck-card--active) .mp-redesign__puck-card-art {
  width: 54px;
  height: 58px;
}

.mp-redesign__puck-card-art :deep(.puck-shell) {
  width: 100%;
}

.mp-redesign__puck-card-art :deep(.puck-shell::before) {
  opacity: 0;
}

.mp-redesign__puck-card-art :deep(.puck-artwork) {
  filter: none;
}

.mp-redesign__puck-card-art :deep(.well-target::before) {
  display: none;
}

.mp-redesign__puck-card-art :deep(.well-box.occupied) {
  filter: drop-shadow(0 1px 2px rgba(15, 23, 42, 0.2));
}

.mp-redesign__puck-card-art :deep(.well-box.empty) {
  opacity: 0.14;
}

.mp-redesign__mini-puck-empty {
  width: 86px;
  height: 94px;
  border: 1px dashed #b7c2cf;
  border-radius: 999px;
  background:
    radial-gradient(circle at 50% 24%, #e8eef5 0 8%, transparent 9%),
    radial-gradient(circle at 35% 48%, #e8eef5 0 7%, transparent 8%),
    radial-gradient(circle at 65% 48%, #e8eef5 0 7%, transparent 8%),
    radial-gradient(circle at 50% 72%, #e8eef5 0 8%, transparent 9%),
    #f8fafc;
}

.mp-redesign__puck-strip--selected .mp-redesign__puck-card:not(.mp-redesign__puck-card--active) .mp-redesign__mini-puck-empty {
  width: 38px;
  height: 42px;
}

.mp-redesign__puck-card strong,
.mp-redesign__puck-card small {
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mp-redesign__puck-card strong {
  font-size: 0.82rem;
  font-weight: 900;
}

.mp-redesign__puck-card small {
  color: #475569;
  font-size: 0.72rem;
  font-weight: 800;
}

.mp-redesign__section-header,
.mp-redesign__stage-header,
.mp-redesign__detail-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.mp-redesign__section-header h3,
.mp-redesign__detail-header h2,
.mp-redesign__stage-header h1 {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 800;
  color: #111827;
}

.mp-redesign__stage-header h1 {
  font-size: 1.2rem;
}

.mp-redesign__stage .mp-redesign__stage-header {
  flex-wrap: wrap;
  padding: 12px 14px;
  border-bottom: 1px solid #dbe3ef;
}

.mp-redesign__stage-summary {
  display: block;
  margin-top: 2px;
  color: #526173;
  font-size: 12px;
  font-weight: 700;
}

.mp-redesign__edit-puck-command {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  min-height: 32px;
  padding: 0 11px;
  border: 1px solid #bfdbfe;
  border-radius: 999px;
  background: #ffffff;
  color: #184f88;
  font: inherit;
  font-size: 12px;
  font-weight: 900;
  cursor: pointer;
}

.mp-redesign__edit-puck-command:hover {
  border-color: #93c5fd;
  background: #eff8ff;
}

.mp-redesign__edit-puck-command:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.mp-redesign__edit-puck-command .material-symbols-outlined {
  font-size: 17px;
}

.mp-redesign__selected-well-chip {
  flex: 0 0 auto;
  margin: 0;
  padding: 7px 10px;
  border: 1px solid #98c9e6;
  border-radius: 999px;
  background: #eff8ff;
  color: #075985;
  font-size: 12px;
  font-weight: 900;
}

.mp-redesign__selected-well-chip.empty {
  border-color: #d6dde7;
  background: #f1f5f9;
  color: #64748b;
}

.mp-redesign__eyebrow {
  margin: 0 0 2px;
  color: #64748b;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0;
  text-transform: uppercase;
}

.mp-redesign__list {
  display: grid;
  gap: 6px;
  margin-top: 8px;
}

.mp-redesign__list-row,
.mp-redesign__well,
.mp-redesign__grid-slot,
.mp-redesign__button,
.mp-redesign__icon-button,
.mp-redesign__create-box {
  border: 1px solid #d8dee9;
  background: #ffffff;
  color: #111827;
  width: auto;
  max-width: none;
  height: auto;
  max-height: none;
  min-height: 0;
  margin: 0;
  font: inherit;
  cursor: pointer;
  transition: border-color 0.15s ease, background-color 0.15s ease, box-shadow 0.15s ease;
}

.mp-redesign__list-row {
  width: 100%;
  display: grid;
  grid-template-columns: 28px minmax(0, 1fr) 20px;
  align-items: center;
  gap: 8px;
  border-radius: 7px;
  padding: 8px;
  text-align: left;
}

.mp-redesign__list-row strong,
.mp-redesign__grid-slot strong,
.mp-redesign__well strong {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mp-redesign__list-row small,
.mp-redesign__well small {
  display: block;
  margin-top: 2px;
  color: #64748b;
  font-size: 0.75rem;
}

.mp-redesign__list-row:hover,
.mp-redesign__well:hover,
.mp-redesign__grid-slot:hover,
.mp-redesign__button:hover,
.mp-redesign__icon-button:hover,
.mp-redesign__create-box:hover {
  border-color: #2563eb;
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.12);
}

.mp-redesign__list-row--active,
.mp-redesign__well--active,
.mp-redesign__grid-slot--active {
  border-color: #2563eb;
  background: #eff6ff;
}

.mp-redesign__row-icon,
.mp-redesign__lock,
.mp-redesign__well-lock {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
}

.mp-redesign__row-icon img {
  width: 24px;
  height: 24px;
  object-fit: contain;
}

.mp-redesign__row-icon--puck {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: var(--puck-color, #dbe4eb);
  box-shadow: inset 0 0 0 1px rgba(15, 23, 42, 0.12);
}

.mp-redesign__row-icon--puck img {
  width: 28px;
  height: 28px;
  mix-blend-mode: multiply;
}

.mp-redesign__stage {
  padding: 0;
  min-height: 0;
}

.mp-redesign__puck-stage {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(210px, 260px);
  gap: 14px;
  margin: 0;
  padding: 14px;
}

.mp-redesign__puck-visual {
  position: relative;
  display: grid;
  place-items: center;
  min-height: 350px;
  border: 1px solid #edf1f6;
  border-radius: 8px;
  background: #f8fafc;
  overflow: visible;
}

.mp-redesign__puck-visual::before {
  display: none;
}

.mp-redesign__puck-visual :deep(.puck-shell) {
  width: min(100%, 320px);
}

.mp-redesign__puck-asset {
  position: absolute;
  inset: 19%;
  z-index: 0;
  width: 62%;
  height: 62%;
  object-fit: contain;
  opacity: 0.16;
  filter: grayscale(1) contrast(1.08);
  mix-blend-mode: multiply;
}

.mp-redesign__well-orb {
  position: relative;
  position: absolute;
  z-index: 1;
  display: grid;
  place-items: center;
  width: 52px;
  height: 52px;
  transform: translate(-50%, -50%);
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: #111827;
  font-weight: 900;
  text-shadow: 0 1px 2px rgba(255, 255, 255, 0.9);
}

.mp-redesign__well-orb--occupied {
  color: #020617;
}

.mp-redesign__well-orb--active {
  filter: drop-shadow(0 0 0 rgba(0, 0, 0, 0));
}

.mp-redesign__well-orb--active::after {
  content: "";
  position: absolute;
  inset: -5px;
  border: 3px solid #38bdf8;
  border-radius: 50%;
  box-shadow: 0 0 0 4px rgba(56, 189, 248, 0.18);
}

.mp-redesign__box-svg {
  position: absolute;
  inset: 1px;
  width: 50px;
  height: 50px;
  z-index: 0;
  filter: drop-shadow(0 7px 10px rgba(15, 23, 42, 0.16));
}

.mp-redesign__box-svg--empty {
  opacity: 0.7;
}

.mp-redesign__well-orb span {
  position: relative;
  z-index: 1;
}

.mp-redesign__well-orb:deep(.boxonlysvg .st0),
.mp-redesign__well-orb:deep(.boxonlysvg .st1) {
  fill: var(--box-color, #cfd8df);
}

.mp-redesign__well-orb:deep(.boxonlysvg .stblack) {
  fill: #111827 !important;
}

.mp-redesign__well-table {
  display: grid;
  align-content: start;
  border: 1px solid #d8e0ea;
  border-radius: 8px;
  overflow: hidden;
  background: #ffffff;
}

.mp-redesign__well-table-head,
.mp-redesign__well-row {
  display: grid;
  grid-template-columns: 46px minmax(0, 1fr) 44px;
  align-items: center;
  gap: 8px;
}

.mp-redesign__well-table-head {
  min-height: 34px;
  padding: 0 10px;
  border-bottom: 1px solid #dbe3ef;
  background: #f1f5f9;
  color: #58708f;
  font-size: 0.72rem;
  font-weight: 900;
  text-transform: uppercase;
}

.mp-redesign__well-row {
  min-height: 34px;
  border: 0;
  border-top: 1px solid #e2e8f0;
  background: #ffffff;
  padding: 0 10px;
  text-align: left;
  font-size: 0.86rem;
}

.mp-redesign__well-row--active {
  background: #edf8ff;
  box-shadow: inset 0 0 0 2px #0ea5e9;
}

.mp-redesign__well-row strong,
.mp-redesign__well-row small {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mp-redesign__well-no {
  display: inline-grid;
  place-items: center;
  width: 26px;
  height: 24px;
  border: 1px solid #cbd5e1;
  border-radius: 50%;
  background: #ffffff;
  color: #475569;
  font-size: 0.78rem;
  font-weight: 900;
}

.mp-redesign__well-row--active .mp-redesign__well-no {
  border-color: #0ea5e9;
  background: #0ea5e9;
  color: #ffffff;
}

.mp-redesign__inspector {
  display: flex;
  flex-direction: column;
  align-content: stretch;
  gap: 0;
  min-width: 0;
  min-height: 0;
}

.mp-redesign__inspector > .mp-redesign__detail-panel:first-child {
  border: 0;
  border-radius: 0;
  background: transparent;
  box-shadow: none;
  padding: 0;
}

.mp-redesign__tabs {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 4px;
  margin: 10px 12px;
  padding: 4px;
  border: 1px solid #dbe3ef;
  border-radius: 8px;
  background: #edf1f6;
}

.mp-redesign__tabs button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  min-width: 0;
  min-height: 31px;
  width: 100%;
  max-width: none;
  height: auto;
  max-height: none;
  margin: 0;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: #64748b;
  font-size: 12px;
  font-weight: 900;
}

.mp-redesign__tabs button:disabled {
  cursor: not-allowed;
  opacity: 0.52;
}

.mp-redesign__tabs .mp-redesign__tab--active {
  background: #ffffff;
  color: #172033;
  box-shadow: 0 1px 4px rgba(15, 23, 42, 0.1);
}

.mp-redesign__detail-panel {
  padding: 12px;
}

.mp-redesign__inspector > .mp-redesign__detail-panel:first-child > .mp-redesign__detail-header {
  padding: 14px;
  border-bottom: 1px solid #dbe3ef;
}

.mp-redesign__selected-summary {
  display: block;
  overflow: hidden;
  max-width: 220px;
  margin-top: 2px;
  color: #526173;
  font-size: 12px;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mp-redesign__form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  margin-top: 10px;
}

.mp-redesign__form-grid--single {
  grid-template-columns: 1fr;
}

.mp-redesign__form-grid label {
  display: grid;
  gap: 4px;
  min-width: 0;
}

.mp-redesign__form-grid label span,
.mp-redesign__checkbox span {
  color: #475569;
  font-size: 0.75rem;
  font-weight: 800;
}

.mp-redesign__form-grid input,
.mp-redesign__form-grid select {
  width: 100%;
  min-width: 0;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  background: #ffffff;
  padding: 8px 9px;
  color: #111827;
}

.mp-redesign__drawer-layer {
  position: fixed;
  inset: 0;
  z-index: 90;
  display: flex;
  justify-content: flex-end;
  background: rgba(15, 23, 42, 0.18);
}

.mp-redesign__entity-drawer {
  display: grid;
  grid-template-rows: auto minmax(0, 1fr) auto;
  width: min(420px, 100vw);
  height: 100vh;
  border-left: 1px solid #cbd5e1;
  background: #ffffff;
  box-shadow: -18px 0 36px rgba(15, 23, 42, 0.18);
}

.mp-redesign__drawer-header {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 12px;
  align-items: start;
  border-bottom: 1px solid #dbe3ef;
  padding: 16px;
}

.mp-redesign__drawer-header h2 {
  margin: 0;
  overflow: hidden;
  color: #172033;
  font-size: 1.08rem;
  font-weight: 900;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mp-redesign__drawer-header span:not(.material-symbols-outlined):not(.mp-redesign__status) {
  display: block;
  margin-top: 3px;
  color: #64748b;
  font-size: 0.78rem;
  font-weight: 800;
}

.mp-redesign__drawer-header-actions {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.mp-redesign__drawer-form {
  align-content: start;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  min-height: 0;
  overflow-y: auto;
  padding: 16px;
}

.mp-redesign__drawer-form label {
  display: grid;
  gap: 5px;
  min-width: 0;
}

.mp-redesign__drawer-form label span {
  color: #475569;
  font-size: 0.75rem;
  font-weight: 900;
}

.mp-redesign__drawer-form input,
.mp-redesign__drawer-form select {
  width: 100%;
  min-width: 0;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  background: #ffffff;
  padding: 9px 10px;
  color: #111827;
}

.mp-redesign__drawer-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  border-top: 1px solid #dbe3ef;
  padding: 12px 16px;
}

.mp-redesign__move-panel,
.mp-redesign__import-panel {
  display: grid;
  gap: 8px;
  margin-top: 12px;
  padding: 10px;
  border: 1px solid #d8e0ea;
  border-radius: 8px;
  background: #f8fafc;
}

.mp-redesign__move-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.mp-redesign__move-panel label,
.mp-redesign__import-panel label {
  display: grid;
  gap: 4px;
}

.mp-redesign__move-panel label span,
.mp-redesign__import-panel label span {
  color: #475569;
  font-size: 0.75rem;
  font-weight: 800;
}

.mp-redesign__move-panel select,
.mp-redesign__import-panel select {
  width: 100%;
  min-width: 0;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  background: #ffffff;
  padding: 8px 9px;
  color: #111827;
}

.mp-redesign__box-preview {
  display: grid;
  place-items: center;
  min-height: 132px;
  border: 1px solid #dbe3ef;
  border-radius: 8px;
  background: #ffffff;
}

.mp-redesign__box-preview-svg {
  width: 92px;
  height: 92px;
  filter: drop-shadow(0 10px 14px rgba(15, 23, 42, 0.16));
}

.mp-redesign__box-preview:deep(.boxonlysvg .st0),
.mp-redesign__box-preview:deep(.boxonlysvg .st1) {
  fill: var(--box-color, #ffd700);
}

.mp-redesign__box-preview:deep(.boxonlysvg .stblack) {
  fill: #111827 !important;
}

.mp-redesign__box-preferences {
  display: grid;
  gap: 10px;
  margin-top: 12px;
  padding: 10px;
  border: 1px solid #dbe3ef;
  border-radius: 8px;
  background: #f8fafc;
}

.mp-redesign__preferences-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.mp-redesign__preferences-header strong,
.mp-redesign__preferences-header span {
  display: block;
}

.mp-redesign__preferences-header strong {
  color: #0f3f66;
  font-size: 0.8rem;
  font-weight: 900;
}

.mp-redesign__preferences-header span {
  margin-top: 2px;
  color: #64748b;
  font-size: 0.7rem;
  font-weight: 800;
}

.mp-redesign__template-select,
.mp-redesign__numbering-option {
  display: grid;
  gap: 4px;
}

.mp-redesign__template-select > span,
.mp-redesign__numbering-options legend {
  color: #475569;
  font-size: 0.72rem;
  font-weight: 900;
  text-transform: uppercase;
}

.mp-redesign__template-select select {
  width: 100%;
  min-width: 0;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  background: #ffffff;
  padding: 8px 9px;
  color: #111827;
}

.mp-redesign__numbering-options {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 7px 10px;
  min-width: 0;
  margin: 0;
  border: 0;
  padding: 0;
}

.mp-redesign__numbering-options legend {
  grid-column: 1 / -1;
  padding: 0;
}

.mp-redesign__numbering-option {
  grid-template-columns: auto minmax(0, 1fr);
  align-items: center;
  color: #172033;
  font-size: 0.74rem;
  font-weight: 800;
}

.mp-redesign__numbering-option input {
  width: auto;
}

.mp-redesign__numbering-option span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mp-redesign__color-swatch-grid {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 6px;
}

.mp-redesign__color-swatch {
  display: grid;
  place-items: center;
  min-height: 28px;
  border: 1px solid #cbd5e1;
  border-radius: 7px;
  background: var(--swatch, #ffd700);
  cursor: pointer;
}

.mp-redesign__color-swatch span {
  width: 12px;
  height: 12px;
  border: 2px solid #ffffff;
  border-radius: 999px;
  background: color-mix(in srgb, var(--swatch-dark, #9ca3af), #000000 4%);
  box-shadow: 0 0 0 1px rgba(15, 23, 42, 0.18);
}

.mp-redesign__color-swatch--active {
  border-color: #0ea5e9;
  box-shadow: 0 0 0 2px rgba(14, 165, 233, 0.18);
}

.mp-redesign__action-strip {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
  margin-top: 10px;
}

.mp-redesign__action-strip .mp-redesign__button {
  min-height: 34px;
  border-color: transparent;
  background: #7bb8b2;
  color: #ffffff;
  font-size: 0.82rem;
  font-weight: 900;
}

.mp-redesign__action-strip .mp-redesign__button--danger {
  background: #d98282;
  color: #ffffff;
}

.mp-redesign__form-grid input:disabled,
.mp-redesign__form-grid select:disabled,
.mp-redesign__drawer-form input:disabled,
.mp-redesign__drawer-form select:disabled,
.mp-redesign__button:disabled,
.mp-redesign__icon-button:disabled,
.mp-redesign__create-box:disabled,
.mp-redesign__color-swatch:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.mp-redesign__checkbox {
  display: inline-flex !important;
  grid-template-columns: auto 1fr;
  align-items: center;
  gap: 8px;
}

.mp-redesign__checkbox--inline {
  display: inline-flex;
}

.mp-redesign__checkbox input {
  width: auto;
}

.mp-redesign__button,
.mp-redesign__create-box {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: 36px;
  border-radius: 7px;
  padding: 7px 11px;
  font-weight: 800;
}

.mp-redesign__create-box {
  width: 100%;
  min-height: 38px;
  margin-top: 12px;
  border-color: #0f766e;
  background: #0f766e;
  color: #ffffff;
  font-weight: 900;
}

.mp-redesign__button--danger {
  border-color: #fecaca;
  background: #fff1f2;
  color: #991b1b;
}

.mp-redesign__icon-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 7px;
}

.mp-redesign__sync,
.mp-redesign__status {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  min-height: 28px;
  border-radius: 999px;
  padding: 4px 9px;
  background: #f1f5f9;
  color: #475569;
  font-size: 0.75rem;
  font-weight: 800;
}

.mp-redesign__occupancy-chip {
  flex: 0 0 auto;
  padding: 5px 9px;
  border: 1px solid #bde5cf;
  border-radius: 999px;
  background: #f2fbf6;
  color: #10783d;
  font-size: 11px;
  font-weight: 900;
}

.mp-redesign__occupancy-chip.empty {
  border-color: #d6dde7;
  background: #f1f5f9;
  color: #64748b;
}

.mp-redesign__occupancy-chip.unavailable {
  border-color: #d6dde7;
  background: #f8fafc;
  color: #64748b;
}

.mp-redesign__sync {
  position: fixed;
  top: 18px;
  right: 248px;
  z-index: 55;
  min-height: 24px;
  background: #ffffff;
}

.mp-redesign__sync[data-state="synced"],
.mp-redesign__status[data-state="saved"],
.mp-redesign__status[data-state="idle"] {
  background: #ecfdf5;
  color: #047857;
}

.mp-redesign__sync[data-state="connecting"],
.mp-redesign__status[data-state="pending"],
.mp-redesign__status[data-state="saving"] {
  background: #eff6ff;
  color: #1d4ed8;
}

.mp-redesign__status[data-state="error"] {
  background: #fef2f2;
  color: #b91c1c;
}

.mp-redesign__empty,
.mp-redesign__empty-stage {
  color: #64748b;
  font-size: 0.86rem;
}

.mp-redesign__empty-stage {
  min-height: 300px;
  display: grid;
  place-items: center;
  text-align: center;
}

.mp-redesign__empty-stage .material-symbols-outlined {
  display: block;
  font-size: 42px;
  margin-bottom: 8px;
  color: #94a3b8;
}

.mp-redesign__box-editor,
.mp-redesign__grid-editor {
  margin-top: 10px;
}

.mp-redesign__grid-slots {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  margin-top: 10px;
}

.mp-redesign__grid-slot {
  min-height: 72px;
  border-radius: 7px;
  padding: 9px;
  text-align: left;
}

.mp-redesign__grid-slot span {
  display: block;
  color: #64748b;
  font-size: 0.74rem;
  font-weight: 800;
  margin-bottom: 5px;
}

.mp-redesign__inspector-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
}

.mp-redesign__create-box {
  width: 100%;
  margin-top: 10px;
  border-color: #0f766e;
  background: #0f766e;
  color: #ffffff;
}

.mp-redesign__create-box--secondary {
  background: #ffffff;
}

.mp-redesign__lamella-summary {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
  margin-top: 10px;
}

.mp-redesign__lamella-summary span {
  display: grid;
  gap: 2px;
  min-width: 0;
  border: 1px solid #d8e0ea;
  border-radius: 7px;
  padding: 8px;
  background: #f8fafc;
  color: #64748b;
  font-size: 0.72rem;
  font-weight: 800;
}

.mp-redesign__lamella-summary strong {
  color: #172033;
  font-size: 1rem;
}

.mp-redesign__lamella-list {
  display: grid;
  gap: 8px;
  margin-top: 12px;
}

.mp-redesign__lamella-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(110px, 140px);
  align-items: center;
  gap: 8px;
  border: 1px solid #d8e0ea;
  border-radius: 8px;
  padding: 9px;
  background: #ffffff;
}

.mp-redesign__lamella-row strong,
.mp-redesign__lamella-row small {
  display: block;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mp-redesign__lamella-row small {
  color: #64748b;
  font-size: 0.74rem;
}

.mp-redesign__lamella-row select {
  width: 100%;
  min-width: 0;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  background: #ffffff;
  padding: 7px 8px;
}

@media (max-width: 1220px) {
  .mp-redesign__layout {
    grid-template-columns: minmax(220px, 280px) minmax(0, 1fr);
  }

  .mp-redesign__inspector {
    grid-column: 1 / -1;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 860px) {
  .mp-redesign__toolbar,
  .mp-redesign__layout,
  .mp-redesign__inspector,
  .mp-redesign__tabs,
  .mp-redesign__puck-stage {
    grid-template-columns: 1fr;
  }

  .mp-redesign__toolbar-actions {
    justify-content: flex-start;
  }

  .mp-redesign__path-strip {
    max-width: 100%;
    min-width: 0;
  }
}

@media (max-width: 560px) {
  .mp-redesign {
    padding: 8px;
  }

  .mp-redesign__form-grid,
  .mp-redesign__drawer-form,
  .mp-redesign__grid-slots {
    grid-template-columns: 1fr;
  }

  .mp-redesign__puck-visual {
    min-height: 300px;
  }

  .mp-redesign__well-orb {
    width: 42px;
    height: 42px;
  }
}
</style>
